import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  FlatList,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import axios from "axios";
import { useAuth } from "./AuthContext";

import { Picker } from "@react-native-picker/picker";
import { Platform } from "react-native";

import {
  PAGE_SIZE,
  USER_COLS,
  DR_COLS,
  SCHOOL_COLS,
  CLASS_COLS,
  PARTNER_COLS,
  BILLING_COLS,
  BILLING_SCHOOL_COLS,
  PLAN_COLS,
  DISCOUNT_COLS,
  CAMP_LINK_COLS,
  USERS_TABLE_MIN_WIDTH,
  DR_TABLE_MIN_WIDTH,
  SCHOOLS_TABLE_MIN_WIDTH,
  CLASSES_TABLE_MIN_WIDTH,
  PARTNERS_TABLE_MIN_WIDTH,
  BILLING_TABLE_MIN_WIDTH,
  BILLING_SCHOOL_TABLE_MIN_WIDTH,
  PLAN_TABLE_MIN_WIDTH,
  DISCOUNT_TABLE_MIN_WIDTH,
  CAMP_LINK_TABLE_MIN_WIDTH,
  normalizeListResponse,
  formatCountLabel,
} from "./components/admin/adminTable";
import LessonLibrarySection from "./components/admin/LessonLibrarySection";
import SeededLessonsPanel from "./components/admin/SeededLessonsPanel";
import AccountTypeBadge from "./components/admin/AccountTypeBadge";
import { ADMIN_CURRICULA } from "./constants/adminCurricula";

// The seeder's own verbs, said the way a person would say them. "skipped" is
// the one that matters: it does not mean the lesson failed, it means the lock
// held and somebody's edit is still there.
const LESSON_SEED_ACTIONS = {
  created: "created",
  updated: "updated",
  skipped: "left alone — edited here",
  validated: "checked, not written",
};
const describeLessonAction = (action) => LESSON_SEED_ACTIONS[action] || action;

const BASE_ROLES = ["student", "teacher", "schooladmin"];
const PARTNER_ADMIN_ROLE = "partneradmin";
const MODERATOR_ROLE = "moderator";
const CURRICULA = ADMIN_CURRICULA;

const InternalAdminPanel = () => {

  const navigation = useNavigation();
  const isWeb = Platform.OS === "web";
  const { user } = useAuth();
  const isInternalAdmin = !!user?.is_internal_admin;
  const isSchoolAdmin = !isInternalAdmin && user?.role === "schooladmin";
  const isPartnerAdmin = !isInternalAdmin && user?.role === PARTNER_ADMIN_ROLE;
  const canAccessAdminPanel =
    !!user && (isInternalAdmin || isPartnerAdmin || isSchoolAdmin);
  const roleOptions = user?.is_internal_admin
    ? [...BASE_ROLES, PARTNER_ADMIN_ROLE, MODERATOR_ROLE]
    : BASE_ROLES;

  const [activeTab, setActiveTab] = useState("users");

  // ── Demo School (internal-admin tool) ──────────────────────────────
  const [demoStatus, setDemoStatus] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false); // action request in flight
  const [demoOptionsOpen, setDemoOptionsOpen] = useState(false);
  const [demoResetFirst, setDemoResetFirst] = useState(false);
  const [demoProvisionLogins, setDemoProvisionLogins] = useState(false);
  const [demoLoginPassword, setDemoLoginPassword] = useState("");
  const [demoResetConfirm, setDemoResetConfirm] = useState("");
  const [demoAccounts, setDemoAccounts] = useState(null); // GET .../accounts payload
  const [demoLoginsOpen, setDemoLoginsOpen] = useState(false);
  const [demoCopiedKey, setDemoCopiedKey] = useState(null);
  const demoCopyTimerRef = useRef(null);

  // ── Lesson Plans (internal-admin tool) ─────────────────────────────
  const [lessonStatus, setLessonStatus] = useState(null); // GET .../seed-status payload
  const [lessonStatusLoading, setLessonStatusLoading] = useState(false);
  const [lessonStatusError, setLessonStatusError] = useState(null);
  const [lessonSeeding, setLessonSeeding] = useState(false); // POST in flight
  const [lessonSeedResult, setLessonSeedResult] = useState(null);
  const [lessonSeedError, setLessonSeedError] = useState(null);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [offset, setOffset] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  const [filters, setFilters] = useState({
    role: "",
    content_db: "",
    school: "",
  });
  const [sortBy, setSortBy] = useState("email");
  const [sortDir, setSortDir] = useState("asc");

  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const EMPTY_EDIT_USER_FORM = {
    first_name: "",
    last_name: "",
    role: "student",
    content_db: "",
    school_id: "",
    science_center: "",
    class_id: "",
    class_role: "",
    partner_id: "",
    grade: "",
    duration: "",
    is_summer_camp: false,
    is_stem_missions: false,
  };
  const [editUserForm, setEditUserForm] = useState(EMPTY_EDIT_USER_FORM);
  const [createUserModalVisible, setCreateUserModalVisible] = useState(false);
  const [bulkUserModalVisible, setBulkUserModalVisible] = useState(false);
  const EMPTY_CREATE_USER_FORM = {
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "student",
    content_db: "",
    school_id: "",
    science_center: "",
    class_id: "",
    class_role: "",
    partner_id: "",
    grade: "",
    duration: "",
    is_summer_camp: false,
    is_stem_missions: false,
  };
  const [createUserForm, setCreateUserForm] = useState(EMPTY_CREATE_USER_FORM);
  const [bulkSchoolId, setBulkSchoolId] = useState("");
  const [bulkPartnerId, setBulkPartnerId] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [userSchoolOptions, setUserSchoolOptions] = useState([]);
  const [userSchoolOptionsLoading, setUserSchoolOptionsLoading] = useState(false);
  const [classOptions, setClassOptions] = useState([]);
  const [classOptionsLoading, setClassOptionsLoading] = useState(false);


  const searchTimeoutRef = useRef(null);

  const [drLoading, setDrLoading] = useState(false);
  const [drResults, setDrResults] = useState([]);
  const [drOffset, setDrOffset] = useState(0);
  const [drHasNext, setDrHasNext] = useState(false);
  const [drTotalCount, setDrTotalCount] = useState(0);

  const [drFilters, setDrFilters] = useState({
    target_type: "",
    student_email: "",
    class_name: "",
    school_name: "",
    status: "",
  });

  const [drModalVisible, setDrModalVisible] = useState(false);

  const EMPTY_DR_FORM = {
    target_type: "student",

    student_email: "",
    class_name: "",
    school_name: "",

    requester_type: "student",
    requester_email: "",

    request_type: "access",
    data_from: "",
    data_to: "",

    retention_days: "",
  };

  const [drForm, setDrForm] = useState(EMPTY_DR_FORM);

  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolResults, setSchoolResults] = useState([]);
  const [schoolOffset, setSchoolOffset] = useState(0);
  const [schoolHasNext, setSchoolHasNext] = useState(false);
  const [schoolTotalCount, setSchoolTotalCount] = useState(0);
  const [schoolModalVisible, setSchoolModalVisible] = useState(false);
  const [schoolEditModalVisible, setSchoolEditModalVisible] = useState(false);
  const EMPTY_SCHOOL_FORM = {
    name: "",
    code: "",
    partner_id: "",
    billing_cycle_days: "",
  };
  const [schoolForm, setSchoolForm] = useState(EMPTY_SCHOOL_FORM);
  const [editingSchoolItem, setEditingSchoolItem] = useState(null);
  const [editSchoolForm, setEditSchoolForm] = useState({
    name: "",
    code: "",
    is_k12: false,
    requires_restricted_mode: false,
  });
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerResults, setPartnerResults] = useState([]);
  const [partnerOffset, setPartnerOffset] = useState(0);
  const [partnerHasNext, setPartnerHasNext] = useState(false);
  const [partnerTotalCount, setPartnerTotalCount] = useState(0);
  const [partnerModalVisible, setPartnerModalVisible] = useState(false);
  const [classLoading, setClassLoading] = useState(false);
  const [classResults, setClassResults] = useState([]);
  const [classOffset, setClassOffset] = useState(0);
  const [classHasNext, setClassHasNext] = useState(false);
  const [classTotalCount, setClassTotalCount] = useState(0);
  const [classModalVisible, setClassModalVisible] = useState(false);
  const [classEditModalVisible, setClassEditModalVisible] = useState(false);
  const EMPTY_CLASS_FORM = {
    name: "",
    code: "",
    school_id: "",
    is_active: true,
    default_password: "",
  };
  const [classForm, setClassForm] = useState(EMPTY_CLASS_FORM);
  const [editingClassItem, setEditingClassItem] = useState(null);
  const [editClassForm, setEditClassForm] = useState({
    name: "",
    code: "",
    is_active: true,
    default_password: "",
  });
  const [classSchoolFilter, setClassSchoolFilter] = useState("");
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingResults, setBillingResults] = useState([]);
  const [billingOffset, setBillingOffset] = useState(0);
  const [billingHasNext, setBillingHasNext] = useState(false);
  const [billingTotalCount, setBillingTotalCount] = useState(0);
  const [billingSchoolResults, setBillingSchoolResults] = useState([]);
  const [billingSchoolOffset, setBillingSchoolOffset] = useState(0);
  const [billingSchoolHasNext, setBillingSchoolHasNext] = useState(false);
  const [billingSchoolTotalCount, setBillingSchoolTotalCount] = useState(0);
  const [billingSubTab, setBillingSubTab] = useState("schools");
  const [paymentsSubTab, setPaymentsSubTab] = useState("plans");
  const [billingUserSchoolFilter, setBillingUserSchoolFilter] = useState("");
  const [paymentChoiceVisible, setPaymentChoiceVisible] = useState(false);
  const [paymentChoiceSchoolId, setPaymentChoiceSchoolId] = useState(null);
  const [partialPaymentVisible, setPartialPaymentVisible] = useState(false);
  const [partialPaymentDays, setPartialPaymentDays] = useState("");
  const [partialPaymentSchoolId, setPartialPaymentSchoolId] = useState(null);
  const [partialPaymentMode, setPartialPaymentMode] = useState("partial");
  const [partialPaymentMax, setPartialPaymentMax] = useState(null);
  const EMPTY_PARTNER_FORM = {
    name: "",
    code: "",
    contact_email: "",
  };
  const [partnerForm, setPartnerForm] = useState(EMPTY_PARTNER_FORM);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerOptionsLoading, setPartnerOptionsLoading] = useState(false);
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);

  const [paymentPlans, setPaymentPlans] = useState([]);
  const [paymentPlansLoading, setPaymentPlansLoading] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountCodesLoading, setDiscountCodesLoading] = useState(false);
  const [renewalsRunning, setRenewalsRunning] = useState(false);
  const [scienceCenterConfigs, setScienceCenterConfigs] = useState([]);
  const [scienceCenterConfigsLoading, setScienceCenterConfigsLoading] = useState(false);
  const [scienceCenterOptions, setScienceCenterOptions] = useState([]);
  const [scienceCenterOptionsLoading, setScienceCenterOptionsLoading] = useState(false);
  const [editingScienceCenterConfig, setEditingScienceCenterConfig] = useState(null);
  const EMPTY_SCIENCE_CENTER_FORM = {
    science_center: "da_vinci",
    day_ordinal: "1",
    collection_id: "",
    advance_threshold_pct: "80",
    subject_label: "",
    quiz_pass_pct: "70",
    ai_questions_target: "0",
    is_active: true,
  };
  const [scienceCenterForm, setScienceCenterForm] = useState(EMPTY_SCIENCE_CENTER_FORM);
  const EMPTY_CAMP_PAYMENT_LINK_FORM = {
    student_name: "",
    parent_name: "",
    shipping_address: "",
    parent_email: "",
    parent_contact_number: "",
    amount_dollars: "295.00",
  };
  const [campLinkSendingIds, setCampLinkSendingIds] = useState([]);
  const [campPaymentLinks, setCampPaymentLinks] = useState([]);
  const [campLinksOffset, setCampLinksOffset] = useState(0);
  const [campLinksTotal, setCampLinksTotal] = useState(0);
  const [campLinksHasNext, setCampLinksHasNext] = useState(false);
  const [campPaymentLinksLoading, setCampPaymentLinksLoading] = useState(false);
  const [campPaymentLinkModalVisible, setCampPaymentLinkModalVisible] = useState(false);
  const [campPaymentLinkForm, setCampPaymentLinkForm] = useState(EMPTY_CAMP_PAYMENT_LINK_FORM);
  const [campPaymentLinkSubmitting, setCampPaymentLinkSubmitting] = useState(false);
  const [campPaymentLinkResult, setCampPaymentLinkResult] = useState(null);

  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const EMPTY_PLAN_FORM = {
    code: "",
    name: "",
    duration_days: "",
    price_dollars: "",
    currency: "USD",
    region: "",
    category: "",
    is_active: true,
  };
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);

  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const EMPTY_DISCOUNT_FORM = {
    code: "",
    discount_type: "flat",
    amount_dollars: "",
    percent_off: "",
    currency: "USD",
    max_redemptions: "",
    ends_at: "",
    is_active: true,
  };
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT_FORM);

  useEffect(() => {
    if (user && !canAccessAdminPanel) {
      navigation.replace("AITutor");
    }
  }, [user, canAccessAdminPanel, navigation]);

  useEffect(() => {
    if (canAccessAdminPanel) {
      loadStudents(0);
    }
  }, [canAccessAdminPanel]);

  const loadStudents = async (newOffset = 0, searchQ = query) => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/students", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
          sort_by: sortBy,
          sort_dir: sortDir,
          ...(searchQ ? { q: searchQ } : {}),
          ...(filters.role ? { role: filters.role } : {}),
          ...(filters.content_db ? { content_db: filters.content_db } : {}),
          ...(filters.school ? { school: filters.school } : {}),
        },
      });

      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > offset && data.length === 0) {
        setHasNext(false);
        return;
      }

      setResults(data);
      setOffset(newOffset);
      setTotalCount(resolvedTotal);
      setHasNext(newOffset + data.length < resolvedTotal);
    } catch {
      showAlert("Error", "Failed to fetch students");
    } finally {
      setLoading(false);
    }
  };

  const loadDataRequests = async (newOffset = 0) => {
    setDrLoading(true);
    try {
      const res = await axios.get("/admin/data-requests", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
          ...(drFilters.target_type && { target_type: drFilters.target_type }),
          ...(drFilters.student_email && {
            student_email: drFilters.student_email,
          }),
          ...(drFilters.class_name && {
            class_name: drFilters.class_name,
          }),
          ...(drFilters.school_name && {
            school_name: drFilters.school_name,
          }),
          ...(drFilters.status && { status: drFilters.status }),
        },
      });

      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > drOffset && data.length === 0) {
        setDrHasNext(false);
        return;
      }

      setDrResults(data);
      setDrOffset(newOffset);
      setDrTotalCount(resolvedTotal);
      setDrHasNext(newOffset + data.length < resolvedTotal);
    } catch {
      showAlert("Error", "Failed to fetch data requests");
    } finally {
      setDrLoading(false);
    }
  };

  const loadSchools = async (newOffset = 0) => {
    setSchoolLoading(true);
    try {
      const res = await axios.get("/admin/schools", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
        },
      });

      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > schoolOffset && data.length === 0) {
        setSchoolHasNext(false);
        return;
      }

      setSchoolResults(data);
      setSchoolOffset(newOffset);
      setSchoolTotalCount(resolvedTotal);
      setSchoolHasNext(newOffset + data.length < resolvedTotal);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch schools";
      showAlert("Error", msg);
    } finally {
      setSchoolLoading(false);
    }
  };

  const loadPartners = async (newOffset = 0) => {
    setPartnerLoading(true);
    try {
      const res = await axios.get("/admin/partners", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
        },
      });

      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > partnerOffset && data.length === 0) {
        setPartnerHasNext(false);
        return;
      }

      setPartnerResults(data);
      setPartnerOffset(newOffset);
      setPartnerTotalCount(resolvedTotal);
      setPartnerHasNext(newOffset + data.length < resolvedTotal);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch partners";
      showAlert("Error", msg);
    } finally {
      setPartnerLoading(false);
    }
  };

  const loadBilling = async (newOffset = 0) => {
    setBillingLoading(true);
    try {
      const res = await axios.get("/admin/billing", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
          ...(billingUserSchoolFilter ? { school: billingUserSchoolFilter } : {}),
        },
      });
      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > billingOffset && data.length === 0) {
        setBillingHasNext(false);
        return;
      }

      setBillingResults(data);
      setBillingOffset(newOffset);
      setBillingTotalCount(resolvedTotal);
      setBillingHasNext(newOffset + data.length < resolvedTotal);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch billing";
      showAlert("Error", msg);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadBillingSchools = async (newOffset = 0) => {
    setBillingLoading(true);
    try {
      const res = await axios.get("/admin/billing/summary", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
        },
      });
      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > billingSchoolOffset && data.length === 0) {
        setBillingSchoolHasNext(false);
        return;
      }

      setBillingSchoolResults(data);
      setBillingSchoolOffset(newOffset);
      setBillingSchoolTotalCount(resolvedTotal);
      setBillingSchoolHasNext(newOffset + data.length < resolvedTotal);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch billing summary";
      showAlert("Error", msg);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadPaymentPlans = async () => {
    if (!user?.is_internal_admin) return;
    setPaymentPlansLoading(true);
    try {
      const res = await axios.get("/admin/payment-plans");
      setPaymentPlans(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch payment plans";
      showAlert("Error", msg);
    } finally {
      setPaymentPlansLoading(false);
    }
  };

  const loadDiscountCodes = async () => {
    if (!user?.is_internal_admin) return;
    setDiscountCodesLoading(true);
    try {
      const res = await axios.get("/admin/discount-codes");
      setDiscountCodes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch discount codes";
      showAlert("Error", msg);
    } finally {
      setDiscountCodesLoading(false);
    }
  };

  const loadScienceCenterConfigs = async () => {
    if (!user?.is_internal_admin) return;
    setScienceCenterConfigsLoading(true);
    try {
      const res = await axios.get("/admin/science-center-collections");
      setScienceCenterConfigs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch science center mappings";
      showAlert("Error", msg);
    } finally {
      setScienceCenterConfigsLoading(false);
    }
  };

  const loadScienceCenterOptions = async () => {
    if (!user?.is_internal_admin) return;
    setScienceCenterOptionsLoading(true);
    try {
      const res = await axios.get("/admin/science-center-collection-options");
      setScienceCenterOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch collection options";
      showAlert("Error", msg);
    } finally {
      setScienceCenterOptionsLoading(false);
    }
  };

  const runManualRenewals = async () => {
    if (!user?.is_internal_admin || renewalsRunning) return;
    setRenewalsRunning(true);
    try {
      await axios.post("/admin/payments/run-renewals");
      showAlert("Success", "Renewals job started.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to start renewals job";
      showAlert("Error", msg);
    } finally {
      setRenewalsRunning(false);
    }
  };

  const loadCampPaymentLinks = async (newOffset = 0) => {
    if (!user?.is_internal_admin) return;
    setCampPaymentLinksLoading(true);
    try {
      const res = await axios.get("/admin/camp-payment-links", {
        params: { offset: newOffset, limit: PAGE_SIZE },
      });
      setCampPaymentLinks(res.data.items || []);
      setCampLinksOffset(res.data.offset);
      setCampLinksTotal(res.data.total);
      setCampLinksHasNext(res.data.has_next);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch camp payment links";
      showAlert("Error", msg);
    } finally {
      setCampPaymentLinksLoading(false);
    }
  };

  const openCampPaymentLinkModal = () => {
    setCampPaymentLinkForm(EMPTY_CAMP_PAYMENT_LINK_FORM);
    setCampPaymentLinkResult(null);
    setCampPaymentLinkModalVisible(true);
  };

  const sendCampPaymentLink = async (row) => {
    if (campLinkSendingIds.includes(row.id)) return;
    setCampLinkSendingIds((ids) => [...ids, row.id]);
    try {
      const res = await axios.post(`/admin/camp-payment-links/${row.id}/send-email`);
      if (!res.data?.email_sent) throw new Error("Payment link could not be emailed. Please try again.");
      setCampPaymentLinks((rows) => rows.map((item) => item.id === row.id ? res.data : item));
      showAlert("Success", "Payment link emailed to the parent.");
    } catch (err) {
      showAlert("Error", err?.response?.data?.detail || err.message || "Failed to send payment link");
    } finally {
      setCampLinkSendingIds((ids) => ids.filter((id) => id !== row.id));
    }
  };

  const createCampPaymentLink = async (sendEmail = false) => {
    if (campPaymentLinkSubmitting) return;
    const student_name = campPaymentLinkForm.student_name.trim();
    const parent_name = campPaymentLinkForm.parent_name.trim();
    const shipping_address = campPaymentLinkForm.shipping_address.trim();
    const parent_email = campPaymentLinkForm.parent_email.trim().toLowerCase();
    const amount_cents = toCents(campPaymentLinkForm.amount_dollars);
    if (!Number.isFinite(amount_cents) || amount_cents <= 0) {
      showAlert("Error", "Enter a valid price greater than zero");
      return;
    }
    if (!student_name || !parent_name || !shipping_address || !parent_email.includes("@")) {
      showAlert("Error", "Student name, parent name, shipping address, and a valid parent email are required");
      return;
    }
    setCampPaymentLinkSubmitting(true);
    try {
      if (campPaymentLinkResult?.id) {
        if (sendEmail) await sendCampPaymentLink(campPaymentLinkResult);
        return;
      }
      const res = await axios.post("/admin/camp-payment-links", {
        student_name, parent_name, shipping_address, parent_email,
        amount_cents,
        parent_contact_number: campPaymentLinkForm.parent_contact_number.trim() || null,
        send_email: !!sendEmail,
      });
      setCampPaymentLinkResult(res.data || null);
      loadCampPaymentLinks();
      showAlert(
        sendEmail && !res.data?.email_sent ? "Email not sent" : "Success",
        sendEmail
          ? res.data?.email_sent ? "Registration created and payment link emailed." : "Registration saved, but email failed. Use Send Link to retry."
          : "Registration created. The payment link is ready."
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showAlert("Error", typeof detail === "string" ? detail : "Failed to create payment link");
    } finally {
      setCampPaymentLinkSubmitting(false);
    }
  };

  const loadPartnerOptions = async () => {
    if (!user?.is_internal_admin) return;
    setPartnerOptionsLoading(true);
    try {
      const res = await axios.get("/admin/partners", {
        params: {
          limit: 500,
          offset: 0,
        },
      });
      const { items } = normalizeListResponse(res.data);
      const data = items;
      setPartnerOptions(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch partners";
      showAlert("Error", msg);
    } finally {
      setPartnerOptionsLoading(false);
    }
  };

  const loadUserSchoolOptions = async () => {
    if (!canAccessAdminPanel) return;
    if (isSchoolAdmin && user?.school_id) {
      setUserSchoolOptions([
        {
          id: user.school_id,
          name: user.school || `School ${user.school_id}`,
          code: user.school_code || "",
        },
      ]);
      return;
    }
    setUserSchoolOptionsLoading(true);
    try {
      const res = await axios.get("/admin/schools", {
        params: {
          limit: 500,
          offset: 0,
        },
      });
      const { items } = normalizeListResponse(res.data);
      const data = items;
      setUserSchoolOptions(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch schools";
      showAlert("Error", msg);
    } finally {
      setUserSchoolOptionsLoading(false);
    }
  };

  const loadClasses = async (newOffset = 0) => {
    setClassLoading(true);
    try {
      const res = await axios.get("/admin/classes", {
        params: {
          limit: PAGE_SIZE,
          offset: newOffset,
          ...(classSchoolFilter ? { school_id: Number(classSchoolFilter) } : {}),
        },
      });

      const { items, total } = normalizeListResponse(res.data);
      const data = items;
      const resolvedTotal = Number.isFinite(total) ? total : data.length;

      if (newOffset > classOffset && data.length === 0) {
        setClassHasNext(false);
        return;
      }

      setClassResults(data);
      setClassOffset(newOffset);
      setClassTotalCount(resolvedTotal);
      setClassHasNext(newOffset + data.length < resolvedTotal);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch classes";
      showAlert("Error", msg);
    } finally {
      setClassLoading(false);
    }
  };

  const loadClassOptions = async (schoolId) => {
    if (!canAccessAdminPanel) return;
    if (!schoolId) {
      setClassOptions([]);
      return;
    }
    setClassOptionsLoading(true);
    try {
      const res = await axios.get("/admin/classes", {
        params: {
          limit: 500,
          offset: 0,
          school_id: Number(schoolId),
        },
      });
      const { items } = normalizeListResponse(res.data);
      setClassOptions(items);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to fetch classes";
      showAlert("Error", msg);
    } finally {
      setClassOptionsLoading(false);
    }
  };

  const onSearchChange = (text) => {
    setQuery(text);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadStudents(0, text.trim());
    }, 500);
  };

  const onSearchClick = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    loadStudents(0, query.trim());
  };

  useEffect(() => {
    if (canAccessAdminPanel && activeTab === "users") {
      loadStudents(0);
    }
  }, [canAccessAdminPanel, activeTab, filters, sortBy, sortDir]);

  useEffect(() => {
    if (isSchoolAdmin && !["users", "classes"].includes(activeTab)) {
      setActiveTab("users");
    }
  }, [isSchoolAdmin, activeTab]);

  useEffect(() => {
    if (canAccessAdminPanel && activeTab === "data_requests") {
      loadDataRequests(0);
    }
  }, [canAccessAdminPanel, activeTab, drFilters]);

  useEffect(() => {
    if (canAccessAdminPanel && activeTab === "schools") {
      loadSchools(0);
    }
  }, [canAccessAdminPanel, activeTab]);

  useEffect(() => {
    if (canAccessAdminPanel && activeTab === "classes") {
      loadClasses(0);
    }
  }, [canAccessAdminPanel, activeTab, classSchoolFilter]);

  useEffect(() => {
    if (activeTab === "classes" && isSchoolAdmin && user?.school_id) {
      setClassSchoolFilter(String(user.school_id));
    }
  }, [activeTab, isSchoolAdmin, user?.school_id]);

  useEffect(() => {
    if (canAccessAdminPanel && user?.is_internal_admin && activeTab === "partners") {
      loadPartners(0);
    }
  }, [canAccessAdminPanel, user?.is_internal_admin, activeTab]);

  useEffect(() => {
    if (canAccessAdminPanel && activeTab === "billing") {
      if (billingSubTab === "schools") {
        loadBillingSchools(0);
      } else {
        loadBilling(0);
      }
    }
  }, [canAccessAdminPanel, activeTab, billingSubTab, billingUserSchoolFilter]);

  useEffect(() => {
    if (canAccessAdminPanel && user?.is_internal_admin && activeTab === "payments") {
      loadPaymentPlans();
      loadDiscountCodes();
      loadCampPaymentLinks();
    }
  }, [canAccessAdminPanel, user?.is_internal_admin, activeTab]);

  useEffect(() => {
    if (canAccessAdminPanel && user?.is_internal_admin && activeTab === "science_centers") {
      loadScienceCenterConfigs();
      loadScienceCenterOptions();
    }
  }, [canAccessAdminPanel, user?.is_internal_admin, activeTab]);

  useEffect(() => {
    if (canAccessAdminPanel && user?.is_internal_admin) {
      loadPartnerOptions();
    }
  }, [canAccessAdminPanel, user?.is_internal_admin]);

  useEffect(() => {
    if (canAccessAdminPanel) {
      loadUserSchoolOptions();
    }
  }, [canAccessAdminPanel]);

  useEffect(() => {
    if (createUserModalVisible) {
      if (isSchoolAdmin && user?.school_id) {
        setCreateUserForm((f) => ({
          ...f,
          school_id: String(user.school_id),
        }));
      }
      const targetSchoolId = createUserForm.school_id || (isSchoolAdmin ? user?.school_id : "");
      if (targetSchoolId) {
        loadClassOptions(targetSchoolId);
      }
    }
  }, [createUserModalVisible, createUserForm.school_id, isSchoolAdmin, user?.school_id]);

  useEffect(() => {
    if (modalVisible) {
      if (isSchoolAdmin && user?.school_id) {
        setEditUserForm((f) => ({
          ...f,
          school_id: String(user.school_id),
        }));
      }
      const targetSchoolId = editUserForm.school_id || (isSchoolAdmin ? user?.school_id : "");
      if (targetSchoolId) {
        loadClassOptions(targetSchoolId);
      }
    }
  }, [modalVisible, editUserForm.school_id, isSchoolAdmin, user?.school_id]);

  const formatMoney = (cents, currency = "USD") => {
    const value = Number(cents || 0) / 100;
    return `$${value.toFixed(2)} ${currency}`;
  };

  const toCents = (val) => {
    const num = Number(String(val || "").replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100);
  };

  const openCreatePlanModal = () => {
    setEditingPlan(null);
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanModalVisible(true);
  };

  const resetScienceCenterForm = () => {
    setEditingScienceCenterConfig(null);
    setScienceCenterForm(EMPTY_SCIENCE_CENTER_FORM);
  };

  const startEditScienceCenterConfig = (row, dayOrdinal) => {
    setEditingScienceCenterConfig(row);
    const fallbackDay = dayOrdinal || row?.day_ordinal || 1;
    setScienceCenterForm({
      science_center: row?.science_center || "",
      day_ordinal: String(row?.day_ordinal || fallbackDay),
      collection_id: row?.collection_id ? String(row.collection_id) : "",
      advance_threshold_pct:
        row?.advance_threshold_pct != null ? String(row.advance_threshold_pct) : "80",
      subject_label: row?.subject_label || "",
      quiz_pass_pct: row?.quiz_pass_pct != null ? String(row.quiz_pass_pct) : "70",
      ai_questions_target:
        row?.ai_questions_target != null ? String(row.ai_questions_target) : "0",
      is_active: row?.is_active !== false,
    });
  };

  // Prefill the form to create a brand-new day-row for a given centre/day.
  const startCreateScienceCenterDay = (scienceCenter, dayOrdinal) => {
    setEditingScienceCenterConfig(null);
    setScienceCenterForm({
      ...EMPTY_SCIENCE_CENTER_FORM,
      science_center: scienceCenter || "",
      day_ordinal: String(dayOrdinal || 1),
    });
  };

  const _clampInt = (value, min, max, fallback) => {
    const num = parseInt(String(value), 10);
    if (!Number.isFinite(num)) return fallback;
    return Math.max(min, Math.min(max, num));
  };

  const saveScienceCenterConfig = async () => {
    const science_center = (scienceCenterForm.science_center || "").trim().toLowerCase();
    const collection_id = Number(scienceCenterForm.collection_id);
    const day_ordinal = _clampInt(scienceCenterForm.day_ordinal, 1, 6, 1);
    const advance_threshold_pct = _clampInt(scienceCenterForm.advance_threshold_pct, 1, 100, 80);
    const quiz_pass_pct = _clampInt(scienceCenterForm.quiz_pass_pct, 0, 100, 70);
    const ai_questions_target = _clampInt(scienceCenterForm.ai_questions_target, 0, 50, 0);
    const subject_label = (scienceCenterForm.subject_label || "").trim() || null;
    if (!science_center || !collection_id) {
      showAlert("Error", "Science centre and collection are required");
      return;
    }

    try {
      if (editingScienceCenterConfig?.id) {
        await axios.patch(`/admin/science-center-collections/${editingScienceCenterConfig.id}`, {
          collection_id,
          day_ordinal,
          advance_threshold_pct,
          quiz_pass_pct,
          ai_questions_target,
          subject_label,
          is_active: !!scienceCenterForm.is_active,
        });
      } else {
        await axios.post("/admin/science-center-collections", {
          science_center,
          collection_id,
          day_ordinal,
          advance_threshold_pct,
          quiz_pass_pct,
          ai_questions_target,
          subject_label,
          is_active: !!scienceCenterForm.is_active,
        });
      }
      showAlert("Success", "Science centre mapping saved");
      resetScienceCenterForm();
      loadScienceCenterConfigs();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save science center mapping";
      showAlert("Error", msg);
    }
  };

  // Group the flat config rows by science centre, exposing the day-1..3 slots so
  // each centre renders as three day-rows (filled or empty/"Add").
  const scienceCenterGroups = useMemo(() => {
    const byCenter = new Map();
    (scienceCenterConfigs || []).forEach((row) => {
      const center = row?.science_center || "";
      if (!byCenter.has(center)) {
        byCenter.set(center, { science_center: center, days: {} });
      }
      const day = Number(row?.day_ordinal) || 1;
      byCenter.get(center).days[day] = row;
    });
    return Array.from(byCenter.values()).sort((a, b) =>
      a.science_center.localeCompare(b.science_center)
    );
  }, [scienceCenterConfigs]);

  const openEditPlanModal = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      code: plan.code || "",
      name: plan.name || "",
      duration_days: String(plan.duration_days || ""),
      price_dollars: plan.price_cents != null ? (plan.price_cents / 100).toFixed(2) : "",
      currency: plan.currency || "USD",
      region: plan.region || "",
      category: plan.category || "",
      is_active: !!plan.is_active,
    });
    setPlanModalVisible(true);
  };

  const savePlan = async () => {
    const code = (planForm.code || "").trim().toLowerCase();
    const name = (planForm.name || "").trim();
    const duration = Number(planForm.duration_days || 0);
    const priceCents = toCents(planForm.price_dollars);
    if (!code || !name || !duration || !priceCents) {
      showAlert("Error", "Please fill all required fields");
      return;
    }
    setIsModalSubmitting(true);
    try {
      if (editingPlan) {
        await axios.patch(`/admin/payment-plans/${editingPlan.id}`, {
          name,
          duration_days: duration,
          price_cents: priceCents,
          currency: planForm.currency || "USD",
          region: (planForm.region || "").trim() || null,
          category: (planForm.category || "").trim().toLowerCase() || null,
          is_active: !!planForm.is_active,
        });
      } else {
        await axios.post("/admin/payment-plans", {
          code,
          name,
          duration_days: duration,
          price_cents: priceCents,
          currency: planForm.currency || "USD",
          region: (planForm.region || "").trim() || null,
          category: (planForm.category || "").trim().toLowerCase() || null,
          is_active: !!planForm.is_active,
        });
      }
      setPlanModalVisible(false);
      setEditingPlan(null);
      loadPaymentPlans();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save plan";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const openCreateDiscountModal = () => {
    setEditingDiscount(null);
    setDiscountForm(EMPTY_DISCOUNT_FORM);
    setDiscountModalVisible(true);
  };

  const openEditDiscountModal = (dc) => {
    setEditingDiscount(dc);
    setDiscountForm({
      code: dc.code || "",
      discount_type: dc.discount_type || "flat",
      amount_dollars: dc.amount_cents != null ? (dc.amount_cents / 100).toFixed(2) : "",
      percent_off: dc.percent_off != null ? String(dc.percent_off) : "",
      currency: dc.currency || "USD",
      max_redemptions: dc.max_redemptions != null ? String(dc.max_redemptions) : "",
      ends_at: dc.ends_at ? String(dc.ends_at).slice(0, 10) : "",
      is_active: !!dc.is_active,
    });
    setDiscountModalVisible(true);
  };

  const saveDiscount = async () => {
    const code = (discountForm.code || "").trim().toUpperCase();
    const dtype = (discountForm.discount_type || "").trim().toLowerCase();
    const amountCents = toCents(discountForm.amount_dollars);
    const percentOff = Number(discountForm.percent_off || 0);
    if (!code || !dtype) {
      showAlert("Error", "Please fill all required fields");
      return;
    }
    if (dtype === "flat" && !amountCents) {
      showAlert("Error", "Enter amount for flat discount");
      return;
    }
    if (dtype === "percent" && !percentOff) {
      showAlert("Error", "Enter percent for percent discount");
      return;
    }
    setIsModalSubmitting(true);
    try {
      const payload = {
        code,
        discount_type: dtype,
        amount_cents: dtype === "flat" ? amountCents : null,
        percent_off: dtype === "percent" ? percentOff : null,
        currency: discountForm.currency || "USD",
        max_redemptions: discountForm.max_redemptions ? Number(discountForm.max_redemptions) : null,
        is_active: !!discountForm.is_active,
        ends_at: discountForm.ends_at ? discountForm.ends_at : null,
      };
      if (editingDiscount) {
        await axios.patch(`/admin/discount-codes/${code}`, payload);
      } else {
        await axios.post("/admin/discount-codes", payload);
      }
      setDiscountModalVisible(false);
      setEditingDiscount(null);
      loadDiscountCodes();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save discount";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const resendVerification = async (email) => {
    if (!email) return;
    try {
      await axios.post(`/admin/students/${encodeURIComponent(email)}/resend-verification`);
      showAlert("Success", "Verification email sent");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to resend verification";
      showAlert("Error", msg);
    }
  };

  const markChallengeCompleted = async (item) => {
    if (!item?.email) return;
    try {
      const res = await axios.post(
        `/admin/students/${encodeURIComponent(item.email)}/mark-challenge-completed`,
        {}
      );
      const code = res?.data?.qr_value ? ` Code: ${res.data.qr_value}.` : "";
      const emailNote = res?.data?.email_sent ? " Challenge completion email sent." : " Challenge marked complete, but email was not sent.";
      showAlert("Success", `Challenge marked complete for ${item.email}.${code}${emailNote}`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to mark challenge as completed";
      showAlert("Error", msg);
    }
  };

  const sendCertificate = async (item) => {
    if (!item?.email) return;
    const payload = {
      email: item.email,
      full_name: item.name || item.email,
    };
    try {
      const res = await axios.post("/davinci/send-certificate", payload);
      const emailNote = res?.data?.email_sent
        ? "Certificate email sent."
        : `Certificate resend attempted, but email was not sent. ${res?.data?.django_response_string || ""}`.trim();
      showAlert("Success", `${emailNote} User: ${item.email}`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.django_response_string ||
        err?.response?.data?.message ||
        "Failed to send certificate";
      showAlert("Error", msg);
    }
  };


  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortArrow = (field) =>
    sortBy === field ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const openEditModal = (item) => {
    setEditingUser(item);
    setEditUserForm({
      first_name: item.first_name || "",
      last_name: item.last_name || "",
      role: item.role || "student",
      content_db: item.content_db || "",
      school_id: item.school_id ? String(item.school_id) : "",
      science_center: item.science_center || "",
      class_id: item.class_id ? String(item.class_id) : "",
      class_role: item.class_role || "",
      partner_id: item.partner_id ? String(item.partner_id) : "",
      grade: item.grade || "",
      duration: "",
      is_summer_camp: !!item.is_summer_camp,
      is_stem_missions: !!item.is_stem_missions,
    });
    setModalVisible(true);
  };

  const saveEdit = async () => {
    if (!editingUser || isModalSubmitting) return;

    setIsModalSubmitting(true);
    try {
      await axios.put(`/admin/students/${editingUser.email}`, {
        first_name: editUserForm.first_name || "",
        last_name: editUserForm.last_name || "",
        role: editUserForm.role,
        content_db: editUserForm.content_db || undefined,
        school_id: editUserForm.school_id ? Number(editUserForm.school_id) : undefined,
        science_center: editUserForm.science_center || "",
        class_id: editUserForm.class_id ? Number(editUserForm.class_id) : null,
        class_role: editUserForm.class_id ? (editUserForm.class_role || null) : null,
        partner_id:
          isInternalAdmin && editUserForm.role === PARTNER_ADMIN_ROLE
            ? (editUserForm.partner_id ? Number(editUserForm.partner_id) : null)
            : undefined,
        grade: editUserForm.grade || undefined,
        duration:
          isInternalAdmin && editUserForm.duration
            ? Number(editUserForm.duration)
            : undefined,
        is_summer_camp: !!editUserForm.is_summer_camp,
        is_stem_missions: !!editUserForm.is_stem_missions,
      });

      showAlert("Success", "User updated successfully");
      setModalVisible(false);
      loadStudents(offset);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to update user";

      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const submitDataRequest = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.post("/admin/data-requests", {
        target_type: drForm.target_type,

        student_email:
          drForm.target_type === "student" ? drForm.student_email : undefined,
        class_name:
          drForm.target_type === "class" ? drForm.class_name : undefined,
        school_name:
          drForm.target_type === "school" ? drForm.school_name : undefined,

        requester_type: drForm.requester_type,
        requester_email: drForm.requester_email || undefined,

        request_type: drForm.request_type,
        data_from: drForm.data_from || undefined,
        data_to: drForm.data_to || undefined,

        retention_days:
          drForm.request_type === "soft_delete"
            ? Number(drForm.retention_days)
            : undefined,
      });

      showAlert("Success", "Data request submitted");

      // ✅ CLOSE + CLEAR
      setDrModalVisible(false);
      setDrForm(EMPTY_DR_FORM);
      loadDataRequests(0);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to submit data request";

      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const createStudent = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.post("/admin/students", {
        email: createUserForm.email.trim(),
        password: createUserForm.password,
        first_name: createUserForm.first_name || undefined,
        last_name: createUserForm.last_name || undefined,
        role: createUserForm.role || "student",
        content_db: createUserForm.content_db || undefined,
        school_id: createUserForm.school_id
          ? Number(createUserForm.school_id)
          : undefined,
        science_center: createUserForm.science_center || undefined,
        class_id: createUserForm.class_id
          ? Number(createUserForm.class_id)
          : undefined,
        class_role: createUserForm.class_role || undefined,
        partner_id: createUserForm.partner_id
          ? Number(createUserForm.partner_id)
          : undefined,
        grade: createUserForm.grade || undefined,
        duration: isInternalAdmin && createUserForm.duration
          ? Number(createUserForm.duration)
          : undefined,
        is_summer_camp: !!createUserForm.is_summer_camp,
        is_stem_missions: !!createUserForm.is_stem_missions,
      });

      showAlert("Success", "User created");
      setCreateUserModalVisible(false);
      setCreateUserForm(EMPTY_CREATE_USER_FORM);
      loadStudents(0);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to create student";

      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const openBulkUserModal = () => {
    setBulkSchoolId("");
    setBulkPartnerId("");
    setBulkFile(null);
    setBulkUserModalVisible(true);
  };

  const pickBulkFile = () => {
    if (!isWeb) {
      showAlert("Info", "Bulk file upload is currently supported on web only");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.csv,application/json,text/csv";
    input.onchange = (event) => {
      const selected = event?.target?.files?.[0];
      if (!selected) return;
      const name = String(selected.name || "").toLowerCase();
      const field = name.endsWith(".csv") ? "csv_file" : "json_file";
      setBulkFile({ file: selected, field, name: selected.name });
    };
    input.click();
  };

  const downloadBulkTemplate = (type = "csv") => {
    if (!isWeb) {
      showAlert("Info", "Template download is currently supported on web only");
      return;
    }

    const csvTemplate =
      "email,password,first_name,last_name,role,content_db,grade,age,class_id,class_role,duration\n" +
      "student1@example.com,Pass@123,John,Doe,student,cbse,9,14,22,student,30\n" +
      "student2@example.com,,Jane,Doe,student,cbse,10,15,22,student,30\n";

    const jsonTemplate = JSON.stringify(
      {
        users: [
          {
            email: "student1@example.com",
            password: "Pass@123",
            first_name: "John",
            last_name: "Doe",
            role: "student",
            content_db: "cbse",
            grade: "9",
            age: "14",
            class_id: "22",
            class_role: "student",
            duration: "30",
          },
          {
            email: "student2@example.com",
            password: "",
            first_name: "Jane",
            last_name: "Doe",
            role: "student",
            content_db: "cbse",
            grade: "10",
            age: "15",
            class_id: "22",
            class_role: "student",
            duration: "30",
          },
        ],
      },
      null,
      2
    );

    const content = type === "json" ? jsonTemplate : csvTemplate;
    const filename =
      type === "json"
        ? "bulk_users_template.json"
        : "bulk_users_template.csv";
    const mime =
      type === "json"
        ? "application/json;charset=utf-8"
        : "text/csv;charset=utf-8";

    const blob = new Blob([content], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredBulkSchools =
    user?.is_internal_admin
      ? (bulkPartnerId
          ? userSchoolOptions.filter(
              (s) => String(s?.partner_id || "") === String(bulkPartnerId)
            )
          : userSchoolOptions)
      : userSchoolOptions;

  const isBulkCreateFormValid = () => {
    if (!bulkSchoolId) return false;
    if (!bulkFile?.file || !bulkFile?.field) return false;
    return true;
  };

  const createBulkUsers = async () => {
    if (isModalSubmitting) return;
    if (!isBulkCreateFormValid()) return;

    setIsModalSubmitting(true);
    try {
      const formData = new FormData();
      formData.append(bulkFile.field, bulkFile.file);
      formData.append("school_id", String(bulkSchoolId));
      if (user?.is_internal_admin && bulkPartnerId) {
        formData.append("partner_id", String(bulkPartnerId));
      }

      const res = await axios.post("/admin/students/bulk", formData);
      const createdCount =
        res?.data?.total_created ?? res?.data?.count ?? res?.data?.total_users ?? 0;

      showAlert(
        "Success",
        `Users are being created in background and this may take a while to reflect. Do not re-upload users again unnecessarily. FastAPI created: ${createdCount}`
      );
      setBulkUserModalVisible(false);
      setBulkSchoolId("");
      setBulkPartnerId("");
      setBulkFile(null);
      loadStudents(0);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to create bulk users";
      showAlert("Error", typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const isCreateStudentFormValid = () => {
    const hasCore = createUserForm.email.trim() && createUserForm.password.trim();
    if (!hasCore) return false;

    if (createUserForm.role === "partneradmin") {
      return !!createUserForm.partner_id;
    }

    return !!createUserForm.school_id;
  };

  const isEditStudentFormValid = () => {
    if (!editUserForm.role) return false;
    if (editUserForm.role === PARTNER_ADMIN_ROLE) {
      return !isInternalAdmin || !!editUserForm.partner_id;
    }
    return !!editUserForm.school_id;
  };

  const isDrFormValid = () => {
    if (!drForm.target_type) return false;
    if (!drForm.request_type) return false;

    if (drForm.target_type === "student" && !drForm.student_email.trim())
      return false;
    if (drForm.target_type === "class" && !drForm.class_name.trim())
      return false;
    if (drForm.target_type === "school" && !drForm.school_name.trim())
      return false;

    if (drForm.request_type === "soft_delete" && !drForm.retention_days)
      return false;

    return true;
  };

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  // ── Demo School actions ────────────────────────────────────────────
  const isDemoRunning = demoStatus?.state === "running";

  const fmtDemoTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
    } catch {
      return String(iso);
    }
  };

  const fetchDemoStatus = async () => {
    try {
      const res = await axios.get("/admin/demo-school/status");
      setDemoStatus(res.data || null);
    } catch {
      // Leave last known status; only seed a neutral idle if we have nothing.
      setDemoStatus((prev) =>
        prev ?? {
          state: "idle",
          operation: null,
          step: null,
          started_at: null,
          finished_at: null,
          summary: null,
          error: null,
        }
      );
    }
  };

  const handleDemoActionError = (err, fallback) => {
    if (err?.response?.status === 409) {
      showAlert(
        "Operation in progress",
        "Another Demo School operation is already running. Please wait for it to finish."
      );
      return;
    }
    const detail = err?.response?.data?.detail || err?.response?.data?.error;
    showAlert("Error", detail || fallback);
  };

  const startDemoSeed = async () => {
    if (isDemoRunning || demoBusy) return;
    setDemoBusy(true);
    try {
      await axios.post("/admin/demo-school/seed", {
        reset_first: demoResetFirst,
        provision_logins: demoProvisionLogins,
        login_password:
          demoProvisionLogins && demoLoginPassword ? demoLoginPassword : null,
      });
      setDemoOptionsOpen(false);
      setDemoLoginPassword("");
      await fetchDemoStatus();
    } catch (err) {
      handleDemoActionError(err, "Failed to start demo school build.");
    } finally {
      setDemoBusy(false);
    }
  };

  const startDemoRollForward = async () => {
    if (isDemoRunning || demoBusy) return;
    setDemoBusy(true);
    try {
      await axios.post("/admin/demo-school/roll-forward", {});
      await fetchDemoStatus();
    } catch (err) {
      handleDemoActionError(err, "Failed to start roll-forward.");
    } finally {
      setDemoBusy(false);
    }
  };

  const startDemoReset = async () => {
    if (isDemoRunning || demoBusy || demoResetConfirm !== "RESET") return;
    setDemoBusy(true);
    try {
      await axios.post("/admin/demo-school/reset", { confirm: "RESET" });
      setDemoResetConfirm("");
      await fetchDemoStatus();
    } catch (err) {
      handleDemoActionError(err, "Failed to start reset.");
    } finally {
      setDemoBusy(false);
    }
  };

  const fetchDemoAccounts = async () => {
    try {
      const res = await axios.get("/admin/demo-school/accounts");
      setDemoAccounts(res.data || null);
    } catch {
      // Non-200 (endpoint absent / not authorized) → hide the block silently.
      setDemoAccounts(null);
    }
  };

  const copyToClipboard = async (text) => {
    const value = String(text ?? "");
    if (!value) return false;
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator?.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(value);
        return true;
      }
      if (Platform.OS === "web" && typeof document !== "undefined") {
        // Fallback for insecure contexts / older browsers.
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      }
    } catch {
      // fall through to the manual fallback
    }
    // Native (no clipboard lib bundled): surface the value to copy manually.
    showAlert("Copy", value);
    return false;
  };

  const handleDemoCopy = async (text, key) => {
    const value = String(text ?? "");
    if (!value) return;
    await copyToClipboard(value);
    setDemoCopiedKey(key);
    if (demoCopyTimerRef.current) clearTimeout(demoCopyTimerRef.current);
    demoCopyTimerRef.current = setTimeout(() => setDemoCopiedKey(null), 1200);
  };

  const demoStudentGroups = useMemo(() => {
    const students = demoAccounts?.students || [];
    const groups = new Map();
    for (const st of students) {
      const key = st.archetype || "Other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(st);
    }
    return Array.from(groups.entries()).map(([archetype, rows]) => ({
      archetype,
      rows,
    }));
  }, [demoAccounts]);

  // Fetch demo status + accounts once when the tab is opened.
  useEffect(() => {
    if (canAccessAdminPanel && isInternalAdmin && activeTab === "demo_school") {
      fetchDemoStatus();
      fetchDemoAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessAdminPanel, isInternalAdmin, activeTab]);

  // Refresh the accounts list after a job completes (e.g. seeded flag flips).
  useEffect(() => {
    if (!(canAccessAdminPanel && isInternalAdmin) || activeTab !== "demo_school") return;
    if (demoStatus?.state === "done") fetchDemoAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessAdminPanel, isInternalAdmin, activeTab, demoStatus?.state]);

  // Clear the copy-feedback timer on unmount.
  useEffect(() => {
    return () => {
      if (demoCopyTimerRef.current) clearTimeout(demoCopyTimerRef.current);
    };
  }, []);

  // Poll every 2.5s while an operation is running; stop otherwise.
  useEffect(() => {
    if (!(canAccessAdminPanel && isInternalAdmin) || activeTab !== "demo_school") return;
    if (demoStatus?.state !== "running") return;
    const id = setInterval(fetchDemoStatus, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessAdminPanel, isInternalAdmin, activeTab, demoStatus?.state]);

  // ── Lesson Plan seeding ────────────────────────────────────────────
  // The seed endpoint validates every file before writing and returns 400 with
  // a detail naming the offending file and field. That message is the whole
  // point of validating up front, so it is shown verbatim rather than replaced
  // with a generic failure line.
  const readLessonError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (detail) return JSON.stringify(detail);
    return err?.message || fallback;
  };

  const fetchLessonSeedStatus = async () => {
    setLessonStatusLoading(true);
    try {
      const res = await axios.get("/admin/lesson-plans/seed-status");
      setLessonStatus(res.data || null);
      setLessonStatusError(null);
    } catch (err) {
      setLessonStatus(null);
      setLessonStatusError(
        readLessonError(err, "Could not load lesson seed status.")
      );
    } finally {
      setLessonStatusLoading(false);
    }
  };

  const seedLessonPlans = async () => {
    if (lessonSeeding) return;
    setLessonSeeding(true);
    setLessonSeedError(null);
    setLessonSeedResult(null);
    try {
      // Empty body → seed every file the server has; the call is synchronous,
      // so the response is the finished outcome and there is no job to poll.
      const res = await axios.post("/admin/lesson-plans/seed", {});
      setLessonSeedResult(res.data || null);
      await fetchLessonSeedStatus();
    } catch (err) {
      setLessonSeedError(readLessonError(err, "Failed to seed lesson plans."));
    } finally {
      setLessonSeeding(false);
    }
  };

  const lessonFiles = lessonStatus?.available || [];
  // Top-level error = the whole lesson directory is unreadable/absent.
  const lessonDirError = lessonStatus?.error || null;
  const lessonFilesInDb = lessonFiles.filter((f) => f.in_database).length;
  const hasNoLessonFiles =
    !!lessonStatus && !lessonDirError && lessonFiles.length === 0;
  // Blocked only when we positively know a seed cannot work. A failed status
  // fetch leaves the button live so a transient blip does not strand the user.
  const canSeedLessons =
    !lessonSeeding && !lessonStatusLoading && !hasNoLessonFiles && !lessonDirError;

  // Locked lessons are the ones a seed will refuse to touch. Saying so before
  // the button is pressed is the point of this line.
  const lessonFilesLocked = lessonStatus?.locked || 0;
  const lessonLockedLine = lessonFilesLocked
    ? ` ${lessonFilesLocked} ${
        lessonFilesLocked === 1 ? "is locked by an edit" : "are locked by edits"
      } made in the Lesson Library, and a seed will leave ${
        lessonFilesLocked === 1 ? "it" : "them"
      } alone.`
    : "";

  const lessonSummaryLine = lessonDirError
    ? "Lesson files could not be read from the server."
    : hasNoLessonFiles
    ? "No lesson files found on the server — there is nothing to seed."
    : `${lessonFilesInDb} of ${lessonFiles.length} lesson file${
        lessonFiles.length === 1 ? "" : "s"
      } already in the database.${lessonLockedLine}`;

  /**
   * What the seed actually did, skips included.
   *
   * Counting only created and updated hides the lessons the lock protected: a
   * run that updated eight and skipped three reported "8 lessons updated", and
   * a run where every lesson was locked reported "No lesson plans were
   * changed." next to a green tick — which reads as "nothing needed doing"
   * when the truth is that three edits were deliberately left standing.
   */
  const describeLessonSeed = (result) => {
    if (!result) return "";
    const plural = (n) => (n === 1 ? "" : "s");
    const skipped = result.skipped || 0;

    const wrote = [];
    if (result.created)
      wrote.push(`${result.created} lesson${plural(result.created)} created`);
    if (result.updated)
      wrote.push(`${result.updated} lesson${plural(result.updated)} updated`);

    if (wrote.length) {
      const line = wrote.join(", ");
      return skipped ? `${line}, ${skipped} left alone (edited here)` : line;
    }
    if (skipped)
      return `Nothing was written — ${skipped} lesson${plural(
        skipped
      )} locked by an edit made here, left exactly as ${
        skipped === 1 ? "it is" : "they are"
      }.`;
    return "No lesson plans were changed.";
  };

  // A tick belongs on a run that wrote something. A run that wrote nothing
  // because every lesson was locked is an outcome, not a success.
  const lessonSeedWrote = Boolean(
    lessonSeedResult && (lessonSeedResult.created || lessonSeedResult.updated)
  );

  // Fetch the seed status once when the tab is opened.
  useEffect(() => {
    if (canAccessAdminPanel && isInternalAdmin && activeTab === "lesson_plans") {
      // A previous run's outcome is stale the moment the tab is reopened.
      setLessonSeedResult(null);
      setLessonSeedError(null);
      fetchLessonSeedStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessAdminPanel, isInternalAdmin, activeTab]);

  const exportDataRequest = async (requestId) => {
    try {
      await axios.post(`/admin/data-requests/${requestId}/export`);

      showAlert("Success", "Export started");

      loadDataRequests(drOffset);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to start export";

      showAlert("Error", msg);
    }
  };

  const downloadDataRequest = async (requestId) => {
    try {
      const res = await axios.get(
        `/admin/data-requests/${requestId}/download`
      );

      console.log(res)

      const download_url = res.data?.download_url;

      console.log(download_url)

      if (Platform.OS === "web") {
        window.location.href = download_url;
      } else {
        showAlert("Info", "Download supported only on web for now");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to download export";

      showAlert("Error", msg);
    }
  };

  const softDeleteDataRequest = async (requestId) => {
    try {
      await axios.post(`/admin/data-requests/${requestId}/soft-delete`);

      showAlert("Success", "Soft delete started");

      loadDataRequests(drOffset);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to start soft delete";

      showAlert("Error", msg);
    }
  };

  const createSchool = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      const payload = {
        name: schoolForm.name.trim(),
        code: schoolForm.code.trim(),
      };

      if (user?.is_internal_admin) {
        if (schoolForm.partner_id) {
          payload.partner_id = Number(schoolForm.partner_id);
          if (!schoolForm.billing_cycle_days) {
            showAlert("Error", "Billing cycle is required when partner is selected");
            return;
          }
          payload.billing_cycle_days = Number(schoolForm.billing_cycle_days);
        }
      }

      await axios.post("/admin/schools", payload);
      showAlert("Success", "School created");
      setSchoolModalVisible(false);
      setSchoolForm(EMPTY_SCHOOL_FORM);
      loadSchools(0);
      loadUserSchoolOptions();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to create school";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const createClass = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      const payload = {
        name: classForm.name.trim(),
        code: classForm.code.trim() || undefined,
        school_id: Number(classForm.school_id),
        is_active: !!classForm.is_active,
        default_password: classForm.default_password || undefined,
      };

      await axios.post("/admin/classes", payload);
      showAlert("Success", "Class created");
      setClassModalVisible(false);
      setClassForm(EMPTY_CLASS_FORM);
      loadClasses(0);
      loadClassOptions(payload.school_id);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to create class";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const createPartner = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.post("/admin/partners", {
        name: partnerForm.name.trim(),
        code: partnerForm.code.trim(),
        contact_email: partnerForm.contact_email.trim() || undefined,
      });
      showAlert("Success", "Partner created");
      setPartnerModalVisible(false);
      setPartnerForm(EMPTY_PARTNER_FORM);
      loadPartners(0);
      loadPartnerOptions();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to create partner";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const receiveBilling = async (schoolId, paymentType, paymentDays = null) => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.post("/admin/billing/receive", {
        school_id: schoolId,
        payment_type: paymentType,
        ...(paymentDays != null ? { payment_days: Number(paymentDays) } : {}),
      });
      showAlert("Success", "Billing received. Updates dispatched.");
      loadBilling(billingOffset);
      loadBillingSchools(billingSchoolOffset);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to receive billing";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const confirmReceiveBilling = (schoolId) => {
    if (isModalSubmitting) return;
    setPaymentChoiceSchoolId(schoolId);
    setPaymentChoiceVisible(true);
  };

  const confirmCarryForward = (schoolId) => {
    if (isModalSubmitting) return;
    const confirmAction = async () => {
      if (isModalSubmitting) return;
      setIsModalSubmitting(true);
      try {
        await axios.post("/admin/billing/carry-forward", {
          school_id: schoolId,
        });
        showAlert("Success", "Billing carried forward.");
        loadBilling(billingOffset);
        loadBillingSchools(billingSchoolOffset);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Failed to carry forward billing";
        showAlert("Error", msg);
      } finally {
        setIsModalSubmitting(false);
      }
    };

    const ok = window.confirm(
      "Billing will be carried forward to the next billing cycle."
    );
    if (ok) confirmAction();
  };

  const exportBillingReport = async () => {
    if (isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.post("/admin/billing/export");
      showAlert("Success", "Report generation started. You'll receive it by email.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to start export";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const openEditSchoolModal = (item) => {
    setEditingSchoolItem(item);
    setEditSchoolForm({
      name: item.name || "",
      code: item.code || "",
      is_k12: !!item.is_k12,
      requires_restricted_mode: !!item.requires_restricted_mode,
    });
    setSchoolEditModalVisible(true);
  };

  const openEditClassModal = (item) => {
    setEditingClassItem(item);
    setEditClassForm({
      name: item.name || "",
      code: item.code || "",
      is_active: !!item.is_active,
      default_password: item.default_password || "",
    });
    setClassEditModalVisible(true);
  };

  const updateClass = async () => {
    if (!editingClassItem || isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.put(`/admin/classes/${editingClassItem.id}`, {
        name: editClassForm.name.trim(),
        code: editClassForm.code.trim() || undefined,
        is_active: !!editClassForm.is_active,
        default_password: editClassForm.default_password || undefined,
      });
      showAlert("Success", "Class updated");
      setClassEditModalVisible(false);
      setEditingClassItem(null);
      loadClasses(classOffset);
      if (createUserForm.school_id) {
        loadClassOptions(createUserForm.school_id);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to update class";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const updateSchool = async () => {
    if (!editingSchoolItem || isModalSubmitting) return;
    setIsModalSubmitting(true);
    try {
      await axios.put(`/admin/schools/${editingSchoolItem.id}`, {
        name: editSchoolForm.name.trim(),
        code: editSchoolForm.code.trim(),
        is_k12: !!editSchoolForm.is_k12,
        requires_restricted_mode: !!editSchoolForm.requires_restricted_mode,
      });

      showAlert("Success", "School updated");
      setSchoolEditModalVisible(false);
      setEditingSchoolItem(null);
      loadSchools(schoolOffset);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to update school";
      showAlert("Error", msg);
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const colStyle = (col, align = "center") => [
    styles.colCell,
    {
      minWidth: col.minWidth,
      flexGrow: col.flex,
      flexBasis: 0,
      flexShrink: 0,
    },
    align === "center" ? styles.cellAlignCenter : styles.cellAlignLeft,
  ];

  const HeaderSortable = ({ label, field, col, align = "center" }) => (
    <TouchableOpacity
      style={[
        styles.headerCell,
        ...colStyle(col, align),
      ]}
      onPress={() => toggleSort(field)}
    >
      <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>
        {label}
        {sortArrow(field)}
      </Text>
    </TouchableOpacity>
  );

  const resolveTargetLabel = (item) => {
    if (item.target_type === "student") return item.student_email || "-";
    if (item.target_type === "class") return item.class_name || "-";
    if (item.target_type === "school") return item.school_name || "-";
    return "-";
  };

  const formatDate = (v) =>
    v ? new Date(v).toLocaleDateString() : "-";

  const formatBillingCycleLabel = (days) => {
    const val = Number(days);
    if (val === 30) return "Monthly";
    if (val === 122) return "Quarterly";
    if (val === 183) return "Semi-Annual";
    if (val === 365) return "Annual";
    return days ?? "-";
  };

  const calcDaysRemaining = (dateStr) => {
    if (!dateStr) return "-";
    const target = new Date(dateStr);
    if (Number.isNaN(target.getTime())) return "-";
    const today = new Date();
    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffMs = target.getTime() - today.getTime();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return `${Math.max(0, days)} days`;
  };

  const formatTrialLabel = (val) => {
    if (val === true) return "Trial";
    if (val === false) return "Paid";
    return "-";
  };

  const renderTableCount = (offsetValue, itemsLen, total, label) => (
    <View style={styles.tableMeta}>
      <Text style={styles.countText}>
        {formatCountLabel(offsetValue, itemsLen, total, label)}
      </Text>
    </View>
  );

  const formatEngagementText = (engagement) => {
    const e = engagement || {};
    const totalTime = e.total_time_seconds ?? 0;
    const quizAttempts = e.quiz_attempts ?? 0;
    const aiQuestions = e.ai_questions ?? 0;
    const avgAccuracy = e.average_accuracy ?? "-";

    return `Total time seconds: ${totalTime}
Quiz attempts: ${quizAttempts}
AI questions: ${aiQuestions}
Average accuracy: ${avgAccuracy}`;
  };

  const renderHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <HeaderSortable label="Email" field="email" col={USER_COLS.email} />
      <HeaderSortable label="Role" field="role" col={USER_COLS.role} />
      <HeaderSortable label="School" field="school" col={USER_COLS.school} />
      <View style={[styles.headerCell, ...colStyle(USER_COLS.scienceCenter)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Science Centre</Text>
      </View>
      <HeaderSortable label="Grade" field="grade" col={USER_COLS.grade} align="center" />
      <View style={[styles.headerCell, ...colStyle(USER_COLS.accessEnd, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Access End</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(USER_COLS.trial, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Status</Text>
      </View>
      <HeaderSortable label="Content DB" field="content_db" col={USER_COLS.contentDb} />
      <View
        style={[
          styles.headerCell,
          ...colStyle(USER_COLS.engagement, "center"),
        ]}
      >
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Engagement</Text>
      </View>
      <View
        style={[
          styles.headerCell,
          ...colStyle(USER_COLS.action, "center"),
        ]}
      >
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Actions</Text>
      </View>
    </View>
  );

  const renderDrHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.targetType)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Target Type</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.target)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Target</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.requester)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Requester</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.requestType)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Request Type</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.from, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>From</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.to, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>To</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.status, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Status</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.requested, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Requested</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(DR_COLS.action, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Action</Text>
      </View>
    </View>
  );

  const renderSchoolHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.name)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Name</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.code)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Code</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.partner)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Partner</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.students, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Users</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.engagement, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Engagement</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(SCHOOL_COLS.action, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Action</Text>
      </View>
    </View>
  );


  const renderItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.email)]}>{item.email}</Text>
      <View style={colStyle(USER_COLS.role)}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, styles.cellAlignCenter]}>{item.role || "student"}</Text>
        <AccountTypeBadge accountType={item.account_type} reason={item.account_type_reason} />
      </View>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.school)]}>{item.school || (item.school_id ? `ID ${item.school_id}` : "-")}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.scienceCenter)]}>{item.science_center || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.grade, "center")]}>{item.grade || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.accessEnd, "center")]}>{formatDate(item.access_end_date)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.trial, "center")]}>{formatTrialLabel(item.is_trial)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(USER_COLS.contentDb)]}>{item.content_db || "-"}</Text>
      <Text style={[styles.cell, styles.engagementText, ...colStyle(USER_COLS.engagement)]}>
        {formatEngagementText(item.engagement_logs)}
      </Text>
      <View style={[styles.centerCell, ...colStyle(USER_COLS.action, "center")]}>
        <View style={styles.actionStack}>
          <TouchableOpacity onPress={() => openEditModal(item)}>
            <Text style={styles.editBtn}>Edit</Text>
          </TouchableOpacity>
          {item?.is_verified === false && (
            <TouchableOpacity onPress={() => resendVerification(item.email)}>
              <Text style={styles.linkBtnSmall}>Resend Verify</Text>
            </TouchableOpacity>
          )}
          {item?.science_center ? (
            <TouchableOpacity onPress={() => markChallengeCompleted(item)}>
              <Text style={styles.linkBtnSmall}>Mark Challenge Complete</Text>
            </TouchableOpacity>
          ) : null}
          {item?.davinci_completion_redeemed_at ? (
            <TouchableOpacity onPress={() => sendCertificate(item)}>
              <Text style={styles.linkBtnSmall}>Send Certificate</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );

  const renderDrItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.targetType)]}>{item.target_type}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.target)]}>{resolveTargetLabel(item)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.requester)]}>{item.requester_email || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.requestType)]}>{item.request_type}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.from, "center")]}>{formatDate(item.data_from)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.to, "center")]}>{formatDate(item.data_to)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.status, "center")]}>{item.status}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(DR_COLS.requested, "center")]}>
        {item.requested_at
          ? new Date(item.requested_at).toLocaleDateString()
          : "-"}
      </Text>
      <View style={[styles.centerCell, ...colStyle(DR_COLS.action, "center")]}>
        {item.request_type === "access" && item.status === "pending" && (
          <TouchableOpacity onPress={() => exportDataRequest(item.id)}>
            <Text style={styles.editBtn}>Export</Text>
          </TouchableOpacity>
        )}

        {item.request_type === "access" &&
          item.status === "fulfilled" &&
          item.export_file_path && (
            <TouchableOpacity onPress={() => downloadDataRequest(item.id)}>
              <Text style={styles.editBtn}>Download</Text>
            </TouchableOpacity>
          )}

        {item.request_type === "soft_delete" && item.status === "pending" && (
          <TouchableOpacity onPress={() => softDeleteDataRequest(item.id)}>
            <Text style={[styles.editBtn, { color: "#c0392b" }]}>
              Soft Delete
            </Text>
          </TouchableOpacity>
        )}

        {!(
          (item.request_type === "access" &&
            (item.status === "pending" ||
              (item.status === "fulfilled" && item.export_file_path))) ||
          (item.request_type === "soft_delete" && item.status === "pending")
        ) && <Text style={{ color: "#aaa" }}>—</Text>}
      </View>
    </View>
  );

  const renderSchoolItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(SCHOOL_COLS.name)]}>{item.name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(SCHOOL_COLS.code)]}>{item.code || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(SCHOOL_COLS.partner)]}>{item.partner_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(SCHOOL_COLS.students, "center")]}>{item.student_count ?? 0}</Text>
      <Text style={[styles.cell, styles.engagementText, ...colStyle(SCHOOL_COLS.engagement)]}>
        {formatEngagementText(item.engagement_logs)}
      </Text>
      <View style={[styles.centerCell, ...colStyle(SCHOOL_COLS.action, "center")]}>
        <TouchableOpacity onPress={() => openEditSchoolModal(item)}>
          <Text style={styles.editBtn}>Edit School</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderClassHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(CLASS_COLS.name)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Name</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(CLASS_COLS.code)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Code</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(CLASS_COLS.school)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>School</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(CLASS_COLS.active, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Active</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(CLASS_COLS.action, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Action</Text>
      </View>
    </View>
  );

  const renderClassItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(CLASS_COLS.name)]}>{item.name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(CLASS_COLS.code)]}>{item.code || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(CLASS_COLS.school)]}>{item.school_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(CLASS_COLS.active, "center")]}>{item.is_active ? "Yes" : "No"}</Text>
      <View style={[styles.centerCell, ...colStyle(CLASS_COLS.action, "center")]}>
        <TouchableOpacity onPress={() => openEditClassModal(item)}>
          <Text style={styles.editBtn}>Edit</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderPartnerHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(PARTNER_COLS.name)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Name</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(PARTNER_COLS.code)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Code</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(PARTNER_COLS.contactEmail)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Contact Email</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(PARTNER_COLS.schools, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Schools</Text>
      </View>
    </View>
  );

  const renderPartnerItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(PARTNER_COLS.name)]}>{item.name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(PARTNER_COLS.code)]}>{item.code || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(PARTNER_COLS.contactEmail)]}>{item.contact_email || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(PARTNER_COLS.schools, "center")]}>{item.school_count ?? 0}</Text>
    </View>
  );

  const renderBillingHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.email)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>User</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.partner)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Partner</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.school)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>School</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.billingCycle, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Billing Cycle</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.pending, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Payment Days Due</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.trial, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Status</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.accessEnd, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Access End</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.nextBilling, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Next Billing</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_COLS.remaining, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Usage Days Remaining</Text>
      </View>
    </View>
  );

  const renderBillingSchoolHeader = () => (
    <View style={[styles.row, styles.headerRow, styles.tableRow]}>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.partner)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Partner</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.school)]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>School</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.users, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Users</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.billingCycle, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Billing Cycle</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.pending, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Payment Days Due</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.lastPayment, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Last Payment</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.nextBilling, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Next Billing</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.remaining, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Usage Days Remaining</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.trial, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Trial</Text>
      </View>
      <View style={[styles.headerCell, ...colStyle(BILLING_SCHOOL_COLS.action, "center")]}>
        <Text numberOfLines={1} ellipsizeMode="tail" style={styles.headerText}>Action</Text>
      </View>
    </View>
  );

  const renderBillingSchoolItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.partner)]}>{item.partner_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.school)]}>{item.school_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.users, "center")]}>{item.user_count ?? 0}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.billingCycle, "center")]}>{formatBillingCycleLabel(item.billing_cycle_days)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.pending, "center")]}>{item.pending_billing_days ?? 0}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.lastPayment, "center")]}>{formatDate(item.last_payment_at)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.nextBilling, "center")]}>{formatDate(item.next_billing_date)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.remaining, "center")]}>{calcDaysRemaining(item.next_billing_date)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_SCHOOL_COLS.trial, "center")]}>
        {item.pending_billing_days === 0 && !item.last_payment_at ? "Trial" : "-"}
      </Text>
      <View style={[styles.centerCell, styles.billingActionColumn, ...colStyle(BILLING_SCHOOL_COLS.action, "center")]}>
        {user?.is_internal_admin && item.user_count > 0 && item.next_billing_date ? (
          <>
            <TouchableOpacity
              disabled={isModalSubmitting}
              onPress={() => {
                setPartialPaymentMax(item.billing_cycle_days || null);
                confirmReceiveBilling(item.school_id);
              }}
            >
            <Text style={[styles.linkBtnSmall, isModalSubmitting && styles.disabledActionText]}>
              Complete payment and upgrade plan
            </Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={isModalSubmitting}
              onPress={() => confirmCarryForward(item.school_id)}
            >
              <Text style={[styles.linkBtnSmall, isModalSubmitting && styles.disabledActionText]}>
                Carry forward the billing to next cycle and upgrade plan
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={isModalSubmitting}
              onPress={() => {
                setPartialPaymentSchoolId(item.school_id);
                setPartialPaymentDays("");
                setPartialPaymentMode("record");
                setPartialPaymentMax(item.pending_billing_days ?? 0);
                setPartialPaymentVisible(true);
              }}
            >
              <Text style={[styles.linkBtnSmall, isModalSubmitting && styles.disabledActionText]}>
                Record payment without further upgrade
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <Text style={{ color: "#aaa" }}>—</Text>
        )}
      </View>
    </View>
  );

  const renderBillingItem = ({ item }) => (
    <View style={[styles.row, styles.tableRow]}>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.email)]}>{item.email || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.partner)]}>{item.partner_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.school)]}>{item.school_name || "-"}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.billingCycle, "center")]}>{formatBillingCycleLabel(item.billing_cycle_days)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.pending, "center")]}>{item.pending_billing_days ?? 0}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.trial, "center")]}>{formatTrialLabel(item.is_trial)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.accessEnd, "center")]}>{formatDate(item.access_end_date)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.nextBilling, "center")]}>{formatDate(item.next_billing_date)}</Text>
      <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.cell, ...colStyle(BILLING_COLS.remaining, "center")]}>{calcDaysRemaining(item.next_billing_date)}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsScroll}
        contentContainerStyle={styles.tabs}
      >
        <TouchableOpacity
          style={[
            styles.tabBtn,
            activeTab === "users" && styles.tabActive,
          ]}
          onPress={() => setActiveTab("users")}
        >
          <Text
            style={[
              styles.tabText,
              activeTab === "users" && styles.tabTextActive,
            ]}
          >
            Users
          </Text>
        </TouchableOpacity>

        {(isInternalAdmin || isPartnerAdmin || isSchoolAdmin) && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "classes" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("classes")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "classes" && styles.tabTextActive,
              ]}
            >
              Classes
            </Text>
          </TouchableOpacity>
        )}

        {(isInternalAdmin || isPartnerAdmin) && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "data_requests" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("data_requests")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "data_requests" && styles.tabTextActive,
              ]}
            >
              Data Requests
            </Text>
          </TouchableOpacity>
        )}

        {(isInternalAdmin || isPartnerAdmin) && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "schools" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("schools")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "schools" && styles.tabTextActive,
              ]}
            >
              Schools
            </Text>
          </TouchableOpacity>
        )}

        {isInternalAdmin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "partners" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("partners")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "partners" && styles.tabTextActive,
              ]}
            >
              Partners
            </Text>
          </TouchableOpacity>
        )}

        {(isInternalAdmin || isPartnerAdmin) && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "billing" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("billing")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "billing" && styles.tabTextActive,
              ]}
            >
              Billing
            </Text>
          </TouchableOpacity>
        )}

        {user?.is_internal_admin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "science_centers" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("science_centers")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "science_centers" && styles.tabTextActive,
              ]}
            >
              Science Centres
            </Text>
          </TouchableOpacity>
        )}

        {user?.is_internal_admin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "payments" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("payments")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "payments" && styles.tabTextActive,
              ]}
            >
              Payments & Discount
            </Text>
          </TouchableOpacity>
        )}

        {isInternalAdmin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "demo_school" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("demo_school")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "demo_school" && styles.tabTextActive,
              ]}
            >
              Demo School
            </Text>
          </TouchableOpacity>
        )}

        {isInternalAdmin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "lesson_plans" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("lesson_plans")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "lesson_plans" && styles.tabTextActive,
              ]}
            >
              Lesson Seeding
            </Text>
          </TouchableOpacity>
        )}

        {isInternalAdmin && (
          <TouchableOpacity
            style={[
              styles.tabBtn,
              activeTab === "lesson_library" && styles.tabActive,
            ]}
            onPress={() => setActiveTab("lesson_library")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "lesson_library" && styles.tabTextActive,
              ]}
            >
              Lesson Library
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
      {activeTab === "users" && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Users</Text>
            <View style={styles.sectionActions}>
              <TouchableOpacity
                onPress={() => setCreateUserModalVisible(true)}
                style={styles.primaryBtn}
              >
                <Text style={styles.primaryBtnText}>Create User</Text>
              </TouchableOpacity>
              {(user?.is_internal_admin || user?.role === PARTNER_ADMIN_ROLE) && (
                <TouchableOpacity
                  onPress={openBulkUserModal}
                  style={styles.primaryBtn}
                >
                  <Text style={styles.primaryBtnText}>Create Bulk Users</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          <View style={styles.toolbarRow}>
            <View style={[styles.controlShell, styles.controlCompact]}>
              <Picker
                selectedValue={filters.role}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, role: v }))
                }
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="All roles" value="" />
                {roleOptions.map((r) => (
                  <Picker.Item key={r} label={r} value={r} />
                ))}
              </Picker>
            </View>

            <View style={[styles.controlShell, styles.controlCompact]}>
              <Picker
                selectedValue={filters.content_db}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, content_db: v }))
                }
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="All curricula" value="" />
                {CURRICULA.map((c) => (
                  <Picker.Item key={c} label={c} value={c} />
                ))}
              </Picker>
            </View>

            <View style={[styles.controlShell, styles.controlCompact]}>
              <Picker
                selectedValue={filters.school}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, school: v }))
                }
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="All schools" value="" />
                {userSchoolOptions.map((s) => (
                  <Picker.Item
                    key={s.id}
                    label={s.name}
                    value={s.name}
                  />
                ))}
              </Picker>
            </View>

            <View style={styles.toolbarRight}>
              <TextInput
                value={query}
                onChangeText={onSearchChange}
                placeholder="email or domain"
                autoCapitalize="none"
                style={[styles.controlInput, styles.searchInputCompact]}
              />
              <TouchableOpacity
                style={styles.searchBtnCompact}
                onPress={onSearchClick}
                disabled={loading}
              >
                <Text style={styles.searchBtnText}>Search</Text>
              </TouchableOpacity>
            </View>
          </View>

          {loading && <ActivityIndicator size="large" />}

          {!loading && (
            <>
              {renderTableCount(offset, results.length, totalCount, "users")}
              <ScrollView
                horizontal
                style={styles.tableViewport}
                contentContainerStyle={styles.tableViewportContent}
              >
                <View style={[styles.tableBox, { minWidth: USERS_TABLE_MIN_WIDTH }]}>
                  {renderHeader()}
                  <FlatList
                    data={results}
                    keyExtractor={(item) => item.email}
                    renderItem={renderItem}
                    ListEmptyComponent={
                      <Text style={styles.empty}>No results</Text>
                    }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={offset === 0}
                            onPress={() =>
                              loadStudents(Math.max(0, offset - PAGE_SIZE))
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                offset === 0 && styles.disabled,
                              ]}
                            >
                              Prev
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            disabled={!hasNext}
                            onPress={() => loadStudents(offset + PAGE_SIZE)}
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                !hasNext && styles.disabled,
                              ]}
                            >
                              Next
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    }
                  />
                </View>
              </ScrollView>
            </>
          )}
        </>
      )}

      {activeTab === "classes" && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Classes</Text>
            <View style={styles.sectionActions}>
              <TouchableOpacity
                onPress={() => {
                  setClassForm((f) => ({
                    ...EMPTY_CLASS_FORM,
                    school_id: isSchoolAdmin && user?.school_id ? String(user.school_id) : "",
                  }));
                  setClassModalVisible(true);
                }}
                style={styles.primaryBtn}
              >
                <Text style={styles.primaryBtnText}>Create Class</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.toolbarRow}>
            {!isSchoolAdmin && (
              <View style={[styles.controlShell, styles.controlCompact]}>
                <Picker
                  selectedValue={classSchoolFilter}
                  onValueChange={(v) => setClassSchoolFilter(v)}
                  style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
                >
                  <Picker.Item label="All schools" value="" />
                  {userSchoolOptions.map((s) => (
                    <Picker.Item
                      key={s.id}
                      label={s.name}
                      value={String(s.id)}
                    />
                  ))}
                </Picker>
              </View>
            )}
          </View>

          {classLoading && <ActivityIndicator size="large" />}

          {!classLoading && (
            <>
              {renderTableCount(classOffset, classResults.length, classTotalCount, "classes")}
              <ScrollView
                horizontal
                style={styles.tableViewport}
                contentContainerStyle={styles.tableViewportContent}
              >
                <View style={[styles.tableBox, { minWidth: CLASSES_TABLE_MIN_WIDTH }]}>
                  {renderClassHeader()}
                  <FlatList
                    data={classResults}
                    keyExtractor={(item) => String(item.id)}
                    renderItem={renderClassItem}
                    ListEmptyComponent={
                      <Text style={styles.empty}>No results</Text>
                    }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={classOffset === 0}
                            onPress={() =>
                              loadClasses(Math.max(0, classOffset - PAGE_SIZE))
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                classOffset === 0 && styles.disabled,
                              ]}
                            >
                              Prev
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            disabled={!classHasNext}
                            onPress={() => loadClasses(classOffset + PAGE_SIZE)}
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                !classHasNext && styles.disabled,
                              ]}
                            >
                              Next
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    }
                  />
                </View>
              </ScrollView>
            </>
          )}
        </>
      )}

      {activeTab === "data_requests" && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Data Requests</Text>

            <TouchableOpacity
              onPress={() => setDrModalVisible(true)}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryBtnText}>
                Create Data Request
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.toolbarRow}>
            <View style={[styles.controlShell, styles.controlCompact]}>
              <Picker
                selectedValue={drFilters.target_type}
                onValueChange={(v) =>
                  setDrFilters((f) => ({ ...f, target_type: v }))
                }
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="All targets" value="" />
                <Picker.Item label="Student" value="student" />
                <Picker.Item label="Class" value="class" />
                <Picker.Item label="School" value="school" />
              </Picker>
            </View>

            <View style={[styles.controlShell, styles.controlCompact]}>
              <Picker
                selectedValue={drFilters.status}
                onValueChange={(v) =>
                  setDrFilters((f) => ({ ...f, status: v }))
                }
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="All status" value="" />
                <Picker.Item label="Pending" value="pending" />
                <Picker.Item label="Processing" value="processing" />
                <Picker.Item label="Fulfilled" value="fulfilled" />
                <Picker.Item label="Rejected" value="rejected" />
              </Picker>
            </View>
          </View>

          {drLoading && <ActivityIndicator size="large" />}

          {!drLoading && (
            <>
              {renderTableCount(drOffset, drResults.length, drTotalCount, "data requests")}
              <ScrollView
                horizontal
                style={styles.tableViewport}
                contentContainerStyle={styles.tableViewportContent}
              >
                <View style={[styles.tableBox, { minWidth: DR_TABLE_MIN_WIDTH }]}>
                  {renderDrHeader()}
                  <FlatList
                    data={drResults}
                    keyExtractor={(item) => String(item.id)}
                    renderItem={renderDrItem}
                    ListEmptyComponent={
                      <Text style={styles.empty}>No results</Text>
                    }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={drOffset === 0}
                            onPress={() =>
                              loadDataRequests(
                                Math.max(0, drOffset - PAGE_SIZE)
                              )
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                drOffset === 0 && styles.disabled,
                              ]}
                            >
                              Prev
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            disabled={!drHasNext}
                            onPress={() =>
                              loadDataRequests(drOffset + PAGE_SIZE)
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                !drHasNext && styles.disabled,
                              ]}
                            >
                              Next
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    }
                  />
                </View>
              </ScrollView>
            </>
          )}
        </>
      )}

      {activeTab === "schools" && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Schools</Text>
            <TouchableOpacity
              onPress={() => setSchoolModalVisible(true)}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryBtnText}>Create School</Text>
            </TouchableOpacity>
          </View>

          {schoolLoading && <ActivityIndicator size="large" />}

          {!schoolLoading && (
            <>
              {renderTableCount(schoolOffset, schoolResults.length, schoolTotalCount, "schools")}
              <ScrollView
                horizontal
                style={styles.tableViewport}
                contentContainerStyle={styles.tableViewportContent}
              >
                <View style={[styles.tableBox, { minWidth: SCHOOLS_TABLE_MIN_WIDTH }]}>
                  {renderSchoolHeader()}
                  <FlatList
                    data={schoolResults}
                    keyExtractor={(item) => String(item.id)}
                    renderItem={renderSchoolItem}
                    ListEmptyComponent={
                      <Text style={styles.empty}>No results</Text>
                    }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={schoolOffset === 0}
                            onPress={() =>
                              loadSchools(Math.max(0, schoolOffset - PAGE_SIZE))
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                schoolOffset === 0 && styles.disabled,
                              ]}
                            >
                              Prev
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            disabled={!schoolHasNext}
                            onPress={() => loadSchools(schoolOffset + PAGE_SIZE)}
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                !schoolHasNext && styles.disabled,
                              ]}
                            >
                              Next
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    }
                  />
                </View>
              </ScrollView>
            </>
          )}
        </>
      )}

      {activeTab === "partners" && user?.is_internal_admin && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Partners</Text>
            <TouchableOpacity
              onPress={() => setPartnerModalVisible(true)}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryBtnText}>Create Partner</Text>
            </TouchableOpacity>
          </View>

          {partnerLoading && <ActivityIndicator size="large" />}

          {!partnerLoading && (
            <>
              {renderTableCount(partnerOffset, partnerResults.length, partnerTotalCount, "partners")}
              <ScrollView
                horizontal
                style={styles.tableViewport}
                contentContainerStyle={styles.tableViewportContent}
              >
                <View style={[styles.tableBox, { minWidth: PARTNERS_TABLE_MIN_WIDTH }]}>
                  {renderPartnerHeader()}
                  <FlatList
                    data={partnerResults}
                    keyExtractor={(item) => String(item.id)}
                    renderItem={renderPartnerItem}
                    ListEmptyComponent={
                      <Text style={styles.empty}>No results</Text>
                    }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={partnerOffset === 0}
                            onPress={() =>
                              loadPartners(Math.max(0, partnerOffset - PAGE_SIZE))
                            }
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                partnerOffset === 0 && styles.disabled,
                              ]}
                            >
                              Prev
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            disabled={!partnerHasNext}
                            onPress={() => loadPartners(partnerOffset + PAGE_SIZE)}
                          >
                            <Text
                              style={[
                                styles.pageBtn,
                                !partnerHasNext && styles.disabled,
                              ]}
                            >
                              Next
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    }
                  />
                </View>
              </ScrollView>
            </>
          )}
        </>
      )}

      {activeTab === "billing" && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Billing</Text>
            <TouchableOpacity onPress={exportBillingReport} style={styles.primaryBtn}>
              <Text style={styles.primaryBtnText}>Export Report</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.billingTabs}>
            <TouchableOpacity
              style={[
                styles.billingTabBtn,
                billingSubTab === "schools" && styles.billingTabActive,
              ]}
              onPress={() => setBillingSubTab("schools")}
            >
              <Text
                style={[
                  styles.billingTabText,
                  billingSubTab === "schools" && styles.billingTabTextActive,
                ]}
              >
                Schools
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.billingTabBtn,
                billingSubTab === "users" && styles.billingTabActive,
              ]}
              onPress={() => setBillingSubTab("users")}
            >
              <Text
                style={[
                  styles.billingTabText,
                  billingSubTab === "users" && styles.billingTabTextActive,
                ]}
              >
                Users
              </Text>
            </TouchableOpacity>
          </View>

          {billingLoading && <ActivityIndicator size="large" />}

          {!billingLoading && (
            <>
              {billingSubTab === "schools" && (
                <>
                  {renderTableCount(
                    billingSchoolOffset,
                    billingSchoolResults.length,
                    billingSchoolTotalCount,
                    "schools"
                  )}
                  <ScrollView
                    horizontal
                    style={styles.tableViewport}
                    contentContainerStyle={styles.tableViewportContent}
                  >
                  <View style={[styles.tableBox, { minWidth: BILLING_SCHOOL_TABLE_MIN_WIDTH }]}>
                    {renderBillingSchoolHeader()}
                    <FlatList
                      data={billingSchoolResults}
                      keyExtractor={(item, idx) => `${item.school_id}-${idx}`}
                      renderItem={renderBillingSchoolItem}
                      ListEmptyComponent={
                        <Text style={styles.empty}>No results</Text>
                      }
                      ListFooterComponent={
                        <View style={styles.pagination}>
                          <View style={styles.paginationActions}>
                            <TouchableOpacity
                              disabled={billingSchoolOffset === 0}
                              onPress={() =>
                                loadBillingSchools(Math.max(0, billingSchoolOffset - PAGE_SIZE))
                              }
                            >
                              <Text
                                style={[
                                  styles.pageBtn,
                                  billingSchoolOffset === 0 && styles.disabled,
                                ]}
                              >
                                Prev
                              </Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                              disabled={!billingSchoolHasNext}
                              onPress={() => loadBillingSchools(billingSchoolOffset + PAGE_SIZE)}
                            >
                              <Text
                                style={[
                                  styles.pageBtn,
                                  !billingSchoolHasNext && styles.disabled,
                                ]}
                              >
                                Next
                              </Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      }
                  />
                </View>
              </ScrollView>
                </>
              )}

              {billingSubTab === "users" && (
                <>
                  <View style={styles.tableMeta}>
                    <View style={styles.billingFilterRow}>
                      <View style={[styles.controlShell, styles.controlCompact]}>
                        <Picker
                          selectedValue={billingUserSchoolFilter}
                          onValueChange={(v) => {
                            setBillingUserSchoolFilter(v);
                            setBillingOffset(0);
                          }}
                          style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
                        >
                          <Picker.Item label="All schools" value="" />
                          {userSchoolOptions.map((s) => (
                            <Picker.Item key={s.id} label={s.name} value={s.name} />
                          ))}
                        </Picker>
                      </View>
                    </View>
                    <Text style={styles.countText}>
                      {formatCountLabel(billingOffset, billingResults.length, billingTotalCount, "users")}
                    </Text>
                  </View>
                <ScrollView
                  horizontal
                  style={styles.tableViewport}
                  contentContainerStyle={styles.tableViewportContent}
                >
                  <View style={[styles.tableBox, { minWidth: BILLING_TABLE_MIN_WIDTH }]}>
                    {renderBillingHeader()}
                    <FlatList
                      data={billingResults}
                      keyExtractor={(item, idx) => `${item.email}-${idx}`}
                      renderItem={renderBillingItem}
                      ListEmptyComponent={
                        <Text style={styles.empty}>No results</Text>
                      }
                    ListFooterComponent={
                      <View style={styles.pagination}>
                        <View style={styles.paginationActions}>
                          <TouchableOpacity
                            disabled={billingOffset === 0}
                              onPress={() =>
                                loadBilling(Math.max(0, billingOffset - PAGE_SIZE))
                              }
                            >
                              <Text
                                style={[
                                  styles.pageBtn,
                                  billingOffset === 0 && styles.disabled,
                                ]}
                              >
                                Prev
                              </Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                              disabled={!billingHasNext}
                              onPress={() => loadBilling(billingOffset + PAGE_SIZE)}
                            >
                              <Text
                                style={[
                                  styles.pageBtn,
                                  !billingHasNext && styles.disabled,
                                ]}
                              >
                                Next
                              </Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      }
                  />
                </View>
              </ScrollView>
                </>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "payments" && user?.is_internal_admin && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Payments and Discount</Text>
          </View>

          <View style={styles.billingTabs}>
            <TouchableOpacity
              style={[
                styles.billingTabBtn,
                paymentsSubTab === "plans" && styles.billingTabActive,
              ]}
              onPress={() => setPaymentsSubTab("plans")}
            >
              <Text
                style={[
                  styles.billingTabText,
                  paymentsSubTab === "plans" && styles.billingTabTextActive,
                ]}
              >
                Plans
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.billingTabBtn,
                paymentsSubTab === "discounts" && styles.billingTabActive,
              ]}
              onPress={() => setPaymentsSubTab("discounts")}
            >
              <Text
                style={[
                  styles.billingTabText,
                  paymentsSubTab === "discounts" && styles.billingTabTextActive,
                ]}
              >
                Discount Codes
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.billingTabBtn,
                paymentsSubTab === "camp_links" && styles.billingTabActive,
              ]}
              onPress={() => setPaymentsSubTab("camp_links")}
            >
              <Text
                style={[
                  styles.billingTabText,
                  paymentsSubTab === "camp_links" && styles.billingTabTextActive,
                ]}
              >
                Camp Links
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.sectionHeader}>
            <Text style={styles.sectionSubtitle}>Manual Actions</Text>
            <TouchableOpacity
              onPress={runManualRenewals}
              disabled={renewalsRunning}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryBtnText}>
                {renewalsRunning ? "Running..." : "Run Renewals Now"}
              </Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.paymentsNote}>
            Use this if the daily cron missed auto-renewals.
          </Text>

          {paymentsSubTab === "plans" && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionSubtitle}>Plans</Text>
                <TouchableOpacity onPress={openCreatePlanModal} style={styles.primaryBtn}>
                  <Text style={styles.primaryBtnText}>Add Plan</Text>
                </TouchableOpacity>
              </View>

              {paymentPlansLoading ? (
                <ActivityIndicator size="large" />
              ) : (
                <ScrollView
                  horizontal
                  style={styles.tableViewport}
                  contentContainerStyle={styles.tableViewportContent}
                >
                  <View style={[styles.tableBox, { minWidth: PLAN_TABLE_MIN_WIDTH }]}>
                    <View style={[styles.row, styles.headerRow]}>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.code, "center")]}>
                        <Text style={styles.headerText}>Code</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.name, "left")]}>
                        <Text style={styles.headerText}>Name</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.duration, "center")]}>
                        <Text style={styles.headerText}>Days</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.price, "center")]}>
                        <Text style={styles.headerText}>Price</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.currency, "center")]}>
                        <Text style={styles.headerText}>Currency</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.region, "center")]}>
                        <Text style={styles.headerText}>Region</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.category, "center")]}>
                        <Text style={styles.headerText}>Category</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.active, "center")]}>
                        <Text style={styles.headerText}>Active</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(PLAN_COLS.action, "center")]}>
                        <Text style={styles.headerText}>Action</Text>
                      </View>
                    </View>
                    {paymentPlans.length === 0 ? (
                      <Text style={styles.empty}>No plans found</Text>
                    ) : (
                      paymentPlans.map((p) => (
                        <View key={p.id} style={[styles.row, styles.tableRow]}>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.code, "center")]}>{p.code}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.name, "left")]}>{p.name}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.duration, "center")]}>{p.duration_days}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.price, "center")]}>{formatMoney(p.price_cents, p.currency)}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.currency, "center")]}>{p.currency}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.region, "center")]}>{p.region || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.category, "center")]}>{p.category || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(PLAN_COLS.active, "center")]}>{p.is_active ? "Yes" : "No"}</Text>
                          <TouchableOpacity onPress={() => openEditPlanModal(p)} style={[styles.cell, ...colStyle(PLAN_COLS.action, "center")]}>
                            <Text style={styles.editBtn}>Edit</Text>
                          </TouchableOpacity>
                        </View>
                      ))
                    )}
                  </View>
                </ScrollView>
              )}
            </>
          )}

          {paymentsSubTab === "discounts" && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionSubtitle}>Discount Codes</Text>
                <TouchableOpacity onPress={openCreateDiscountModal} style={styles.primaryBtn}>
                  <Text style={styles.primaryBtnText}>Add Code</Text>
                </TouchableOpacity>
              </View>

              {discountCodesLoading ? (
                <ActivityIndicator size="large" />
              ) : (
                <ScrollView
                  horizontal
                  style={styles.tableViewport}
                  contentContainerStyle={styles.tableViewportContent}
                >
                  <View style={[styles.tableBox, { minWidth: DISCOUNT_TABLE_MIN_WIDTH }]}>
                    <View style={[styles.row, styles.headerRow]}>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.code, "center")]}>
                        <Text style={styles.headerText}>Code</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.type, "center")]}>
                        <Text style={styles.headerText}>Type</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.amount, "center")]}>
                        <Text style={styles.headerText}>Amount</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.percent, "center")]}>
                        <Text style={styles.headerText}>Percent</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.currency, "center")]}>
                        <Text style={styles.headerText}>Currency</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.expires, "center")]}>
                        <Text style={styles.headerText}>Expires</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.active, "center")]}>
                        <Text style={styles.headerText}>Active</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(DISCOUNT_COLS.action, "center")]}>
                        <Text style={styles.headerText}>Action</Text>
                      </View>
                    </View>
                    {discountCodes.length === 0 ? (
                      <Text style={styles.empty}>No codes found</Text>
                    ) : (
                      discountCodes.map((d) => (
                        <View key={d.code} style={[styles.row, styles.tableRow]}>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.code, "center")]}>{d.code}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.type, "center")]}>{d.discount_type}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.amount, "center")]}>{d.amount_cents ? formatMoney(d.amount_cents, d.currency) : "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.percent, "center")]}>{d.percent_off ?? "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.currency, "center")]}>{d.currency}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.expires, "center")]}>{formatDate(d.ends_at)}</Text>
                          <Text style={[styles.cell, ...colStyle(DISCOUNT_COLS.active, "center")]}>{d.is_active ? "Yes" : "No"}</Text>
                          <TouchableOpacity onPress={() => openEditDiscountModal(d)} style={[styles.cell, ...colStyle(DISCOUNT_COLS.action, "center")]}>
                            <Text style={styles.editBtn}>Edit</Text>
                          </TouchableOpacity>
                        </View>
                      ))
                    )}
                  </View>
                </ScrollView>
              )}
            </>
          )}

          {paymentsSubTab === "camp_links" && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionSubtitle}>Camp Registrations</Text>
                <TouchableOpacity onPress={openCampPaymentLinkModal} style={styles.primaryBtn}>
                  <Text style={styles.primaryBtnText}>Get New Link</Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.paymentsNote}>
                This section shows all camp checkout links generated so far, along with payment status.
              </Text>
              {campPaymentLinksLoading ? (
                <ActivityIndicator size="large" />
              ) : (
                <ScrollView
                  horizontal
                  style={styles.tableViewport}
                  contentContainerStyle={styles.tableViewportContent}
                >
                  <View style={[styles.tableBox, { minWidth: CAMP_LINK_TABLE_MIN_WIDTH }]}>
                    <View style={[styles.row, styles.headerRow]}>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.studentName, "left")]}>
                        <Text style={styles.headerText}>Student Name</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.parentName, "left")]}>
                        <Text style={styles.headerText}>Parent Name</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.shippingAddress, "left")]}>
                        <Text style={styles.headerText}>Shipping Address</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.parentContactNumber, "left")]}>
                        <Text style={styles.headerText}>Contact Number</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.parentEmail, "left")]}>
                        <Text style={styles.headerText}>Parent Email</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.amount, "center")]}>
                        <Text style={styles.headerText}>Amount</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.paymentStatus, "center")]}>
                        <Text style={styles.headerText}>Paid</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.linkSent, "center")]}>
                        <Text style={styles.headerText}>Link Emailed</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.createdAt, "center")]}>
                        <Text style={styles.headerText}>Created</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.paidAt, "center")]}>
                        <Text style={styles.headerText}>Paid At</Text>
                      </View>
                      <View style={[styles.headerCell, ...colStyle(CAMP_LINK_COLS.actions, "center")]}>
                        <Text style={styles.headerText}>Actions</Text>
                      </View>
                    </View>
                    {campPaymentLinks.length === 0 ? (
                      <Text style={styles.empty}>No camp registrations yet</Text>
                    ) : (
                      campPaymentLinks.map((row) => (
                        <View key={row.id} style={[styles.row, styles.tableRow]}>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.studentName, "left")]}>{row.student_name || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.parentName, "left")]}>{row.parent_name || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.shippingAddress, "left")]}>{row.shipping_address || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.parentContactNumber, "left")]}>{row.parent_contact_number || "-"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.parentEmail, "left")]}>{row.parent_email}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.amount, "center")]}>{formatMoney(row.amount_total_cents, row.currency)}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.paymentStatus, "center")]}>{(row.paid ?? row.status === "paid") ? "Yes" : "No"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.linkSent, "center")]}>{row.link_email_sent ? "Yes" : "No"}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.createdAt, "center")]}>{formatDate(row.created_at)}</Text>
                          <Text style={[styles.cell, ...colStyle(CAMP_LINK_COLS.paidAt, "center")]}>{formatDate(row.paid_at)}</Text>
                          <View style={[styles.cell, ...colStyle(CAMP_LINK_COLS.actions, "center")]}>
                            {(row.paid ?? row.status === "paid") ? <Text>-</Text> : (
                              <TouchableOpacity
                                onPress={() => sendCampPaymentLink(row)}
                                disabled={campLinkSendingIds.includes(row.id)}
                                style={styles.primaryBtn}
                              >
                                <Text style={styles.primaryBtnText}>{campLinkSendingIds.includes(row.id) ? "Sending..." : "Send Link"}</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        </View>
                      ))
                    )}
                  </View>
                </ScrollView>
              )}
              <View style={styles.pagination}>
                <Text>{campLinksTotal === 0 ? "0 registrations" : `${campLinksOffset + 1}-${campLinksOffset + campPaymentLinks.length} of ${campLinksTotal}`}</Text>
                <View style={styles.paginationActions}>
                  <TouchableOpacity disabled={campPaymentLinksLoading || campLinksOffset === 0}
                    onPress={() => loadCampPaymentLinks(Math.max(0, campLinksOffset - PAGE_SIZE))}>
                    <Text style={[styles.pageBtn, (campPaymentLinksLoading || campLinksOffset === 0) && styles.disabled]}>Prev</Text>
                  </TouchableOpacity>
                  <TouchableOpacity disabled={campPaymentLinksLoading || !campLinksHasNext}
                    onPress={() => loadCampPaymentLinks(campLinksOffset + PAGE_SIZE)}>
                    <Text style={[styles.pageBtn, (campPaymentLinksLoading || !campLinksHasNext) && styles.disabled]}>Next</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}
        </>
      )}

      {activeTab === "science_centers" && user?.is_internal_admin && (
        <>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Science Centre Collection Access</Text>
          </View>

          <Text style={styles.paymentsNote}>
            Students tagged with a science centre at signup or login will automatically receive the mapped collection on later logins too.
          </Text>

          <View style={styles.adminCard}>
            <Text style={styles.sectionSubtitle}>
              {editingScienceCenterConfig ? "Edit Mapping" : "Assign Collection"}
            </Text>

            <View style={styles.toolbarRow}>
              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Science centre</Text>
                <TextInput
                  value={scienceCenterForm.science_center}
                  onChangeText={(v) =>
                    setScienceCenterForm((f) => ({ ...f, science_center: v }))
                  }
                  placeholder="science center code"
                  autoCapitalize="none"
                  style={[styles.controlInput, styles.scienceCenterCodeInput]}
                  editable={!editingScienceCenterConfig}
                />
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Day</Text>
                <View style={[styles.controlShell, styles.sccDayShell]}>
                  <Picker
                    enabled={!editingScienceCenterConfig}
                    selectedValue={String(scienceCenterForm.day_ordinal)}
                    onValueChange={(v) =>
                      setScienceCenterForm((f) => ({ ...f, day_ordinal: v }))
                    }
                    style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
                  >
                    <Picker.Item label="Day 1" value="1" />
                    <Picker.Item label="Day 2" value="2" />
                    <Picker.Item label="Day 3" value="3" />
                    <Picker.Item label="Day 4" value="4" />
                    <Picker.Item label="Day 5" value="5" />
                    <Picker.Item label="Day 6" value="6" />
                  </Picker>
                </View>
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Collection</Text>
                <View style={[styles.controlShell, styles.scienceCenterPickerShell]}>
                  <Picker
                    selectedValue={scienceCenterForm.collection_id}
                    onValueChange={(v) =>
                      setScienceCenterForm((f) => ({ ...f, collection_id: v }))
                    }
                    style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
                  >
                    <Picker.Item
                      label={scienceCenterOptionsLoading ? "Loading collections..." : "Select collection"}
                      value=""
                    />
                    {scienceCenterOptions.map((row) => (
                      <Picker.Item
                        key={row.id}
                        label={`${row.name} (#${row.id})`}
                        value={String(row.id)}
                      />
                    ))}
                  </Picker>
                </View>
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Advance %</Text>
                <TextInput
                  value={String(scienceCenterForm.advance_threshold_pct)}
                  onChangeText={(v) =>
                    setScienceCenterForm((f) => ({
                      ...f,
                      advance_threshold_pct: v.replace(/[^0-9]/g, ""),
                    }))
                  }
                  placeholder="80"
                  keyboardType="numeric"
                  style={[styles.controlInput, styles.sccPctInput]}
                />
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Subject</Text>
                <TextInput
                  value={scienceCenterForm.subject_label}
                  onChangeText={(v) =>
                    setScienceCenterForm((f) => ({ ...f, subject_label: v }))
                  }
                  placeholder="e.g. Biology"
                  style={[styles.controlInput, styles.sccSubjectInput]}
                />
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Quiz pass %</Text>
                <TextInput
                  value={String(scienceCenterForm.quiz_pass_pct)}
                  onChangeText={(v) =>
                    setScienceCenterForm((f) => ({
                      ...f,
                      quiz_pass_pct: v.replace(/[^0-9]/g, ""),
                    }))
                  }
                  placeholder="70"
                  keyboardType="numeric"
                  style={[styles.controlInput, styles.sccPctInput]}
                />
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>AI questions</Text>
                <TextInput
                  value={String(scienceCenterForm.ai_questions_target)}
                  onChangeText={(v) =>
                    setScienceCenterForm((f) => ({
                      ...f,
                      ai_questions_target: v.replace(/[^0-9]/g, ""),
                    }))
                  }
                  placeholder="0"
                  keyboardType="numeric"
                  style={[styles.controlInput, styles.sccPctInput]}
                />
              </View>

              <View style={styles.sccField}>
                <Text style={styles.sccFieldLabel}>Status</Text>
                <View style={[styles.controlShell, styles.controlCompact]}>
                  <Picker
                    selectedValue={scienceCenterForm.is_active ? "active" : "inactive"}
                    onValueChange={(v) =>
                      setScienceCenterForm((f) => ({ ...f, is_active: v === "active" }))
                    }
                    style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
                  >
                    <Picker.Item label="Active" value="active" />
                    <Picker.Item label="Inactive" value="inactive" />
                  </Picker>
                </View>
              </View>
            </View>

            <View style={styles.sectionActions}>
              <TouchableOpacity onPress={saveScienceCenterConfig} style={styles.primaryBtn}>
                <Text style={styles.primaryBtnText}>
                  {editingScienceCenterConfig ? "Update Mapping" : "Save Mapping"}
                </Text>
              </TouchableOpacity>
              {editingScienceCenterConfig ? (
                <TouchableOpacity onPress={resetScienceCenterForm} style={styles.secondaryBtn}>
                  <Text style={styles.secondaryBtnText}>Cancel</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </View>

          {scienceCenterConfigsLoading ? (
            <ActivityIndicator size="large" />
          ) : (
            <View style={styles.adminCard}>
              <Text style={styles.sectionSubtitle}>Current Mappings</Text>
              {scienceCenterGroups.length === 0 ? (
                <Text style={styles.empty}>No science centre mappings yet</Text>
              ) : (
                scienceCenterGroups.map((group) => (
                  <View key={group.science_center} style={styles.sccGroup}>
                    <Text style={styles.sccGroupTitle}>{group.science_center}</Text>
                    {[1, 2, 3, 4, 5, 6].map((day) => {
                      const row = group.days[day];
                      return (
                        <View key={day} style={styles.mappingRow}>
                          <View style={styles.mappingCopy}>
                            <Text style={styles.mappingTitle}>Day {day}</Text>
                            {row ? (
                              <Text style={styles.mappingMeta}>
                                {row.collection_name || "Unknown collection"} (#{row.collection_id})
                                {row.subject_label ? ` • ${row.subject_label}` : ""}
                                {" • "}Advance {row.advance_threshold_pct ?? 80}%
                                {" • "}Quiz pass {row.quiz_pass_pct ?? 70}%
                                {(row.ai_questions_target ?? 0) > 0 ? ` • AI ${row.ai_questions_target}` : ""}
                                {" • "}{row.is_active ? "Active" : "Inactive"}
                              </Text>
                            ) : (
                              <Text style={styles.mappingMeta}>Not configured</Text>
                            )}
                          </View>
                          {row ? (
                            <TouchableOpacity
                              onPress={() => startEditScienceCenterConfig(row, day)}
                              style={styles.secondaryBtn}
                            >
                              <Text style={styles.secondaryBtnText}>Edit</Text>
                            </TouchableOpacity>
                          ) : (
                            <TouchableOpacity
                              onPress={() => startCreateScienceCenterDay(group.science_center, day)}
                              style={styles.secondaryBtn}
                            >
                              <Text style={styles.secondaryBtnText}>Add</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      );
                    })}
                  </View>
                ))
              )}
            </View>
          )}
        </>
      )}

      {activeTab === "demo_school" && isInternalAdmin && (
        <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Demo School</Text>
          </View>

          <Text style={styles.demoDesc}>
            Fictional "Saras Innovation Academy" — 30 students, 4 teachers, 6 months of
            activity. Isolated from real data; safe to rebuild.
          </Text>

          {/* ── Status ── */}
          <View style={styles.adminCard}>
            <Text style={styles.demoCardTitle}>Status</Text>
            {demoStatus == null ? (
              <View style={styles.demoStatusRow}>
                <ActivityIndicator size="small" color="#0077B6" />
                <Text style={styles.demoMuted}>Loading status…</Text>
              </View>
            ) : demoStatus.state === "running" ? (
              <View>
                <View style={styles.demoStatusRow}>
                  <ActivityIndicator size="small" color="#0077B6" />
                  <Text style={styles.demoRunning}>
                    Running: {demoStatus.operation || "operation"}
                    {demoStatus.step ? ` — ${demoStatus.step}` : ""}
                  </Text>
                </View>
                {demoStatus.started_at ? (
                  <Text style={styles.demoMuted}>
                    Started {fmtDemoTime(demoStatus.started_at)}
                  </Text>
                ) : null}
              </View>
            ) : demoStatus.state === "failed" ? (
              <View style={styles.demoErrorBox}>
                <Text style={styles.demoErrorTitle}>
                  Last operation failed
                  {demoStatus.operation ? ` (${demoStatus.operation})` : ""}
                </Text>
                <Text style={styles.demoErrorText}>
                  {demoStatus.error || "Unknown error."}
                </Text>
              </View>
            ) : demoStatus.state === "done" ? (
              <View>
                <Text style={styles.demoSuccess}>
                  ✓ Completed {demoStatus.operation || ""}
                  {demoStatus.finished_at
                    ? ` at ${fmtDemoTime(demoStatus.finished_at)}`
                    : ""}
                </Text>
                {demoStatus.summary &&
                Object.keys(demoStatus.summary).length > 0 ? (
                  <View style={styles.demoSummaryTable}>
                    {Object.entries(demoStatus.summary).map(([tbl, count]) => (
                      <View key={tbl} style={styles.demoSummaryRow}>
                        <Text style={styles.demoSummaryKey}>{tbl}</Text>
                        <Text style={styles.demoSummaryVal}>{String(count)}</Text>
                      </View>
                    ))}
                  </View>
                ) : null}
              </View>
            ) : (
              <Text style={styles.demoMuted}>
                No demo school operation has run yet.
              </Text>
            )}
          </View>

          {/* ── Actions ── */}
          <View style={styles.adminCard}>
            {/* Create / Rebuild */}
            <View style={styles.demoActionRow}>
              <View style={styles.demoActionInfo}>
                <Text style={styles.demoActionTitle}>Create / Rebuild</Text>
                <Text style={styles.demoMuted}>
                  Seeds the full demo school (org, roster, content, 6 months of
                  activity).
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => setDemoOptionsOpen((o) => !o)}
                style={[
                  styles.primaryBtn,
                  (isDemoRunning || demoBusy) && styles.demoBtnDisabled,
                ]}
                disabled={isDemoRunning || demoBusy}
              >
                <Text style={styles.primaryBtnText}>
                  {demoOptionsOpen ? "Hide options" : "Create / Rebuild"}
                </Text>
              </TouchableOpacity>
            </View>

            {demoOptionsOpen && (
              <View style={styles.demoOptions}>
                <TouchableOpacity
                  style={styles.demoCheckRow}
                  onPress={() => setDemoResetFirst((v) => !v)}
                  disabled={isDemoRunning || demoBusy}
                >
                  <View
                    style={[
                      styles.demoCheckbox,
                      demoResetFirst && styles.demoCheckboxOn,
                    ]}
                  >
                    {demoResetFirst ? (
                      <Text style={styles.demoCheckMark}>✓</Text>
                    ) : null}
                  </View>
                  <Text style={styles.demoCheckLabel}>
                    Reset first (delete existing demo data before seeding)
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.demoCheckRow}
                  onPress={() => setDemoProvisionLogins((v) => !v)}
                  disabled={isDemoRunning || demoBusy}
                >
                  <View
                    style={[
                      styles.demoCheckbox,
                      demoProvisionLogins && styles.demoCheckboxOn,
                    ]}
                  >
                    {demoProvisionLogins ? (
                      <Text style={styles.demoCheckMark}>✓</Text>
                    ) : null}
                  </View>
                  <Text style={styles.demoCheckLabel}>
                    Provision logins (Django)
                  </Text>
                </TouchableOpacity>

                <TextInput
                  value={demoLoginPassword}
                  onChangeText={setDemoLoginPassword}
                  placeholder="Login password (optional)"
                  editable={demoProvisionLogins && !isDemoRunning && !demoBusy}
                  secureTextEntry
                  autoCapitalize="none"
                  style={[
                    styles.controlInput,
                    styles.demoPasswordInput,
                    !demoProvisionLogins && styles.demoInputDisabled,
                  ]}
                />

                <TouchableOpacity
                  onPress={startDemoSeed}
                  style={[
                    styles.primaryBtn,
                    styles.demoStartBtn,
                    (isDemoRunning || demoBusy) && styles.demoBtnDisabled,
                  ]}
                  disabled={isDemoRunning || demoBusy}
                >
                  <Text style={styles.primaryBtnText}>
                    {demoBusy ? "Starting…" : "Start build"}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.demoDivider} />

            {/* Roll forward */}
            <View style={styles.demoActionRow}>
              <View style={styles.demoActionInfo}>
                <Text style={styles.demoActionTitle}>Roll forward dates</Text>
                <Text style={styles.demoMuted}>
                  Re-anchors the 6-month history to today — run before demos.
                </Text>
              </View>
              <TouchableOpacity
                onPress={startDemoRollForward}
                style={[
                  styles.secondaryBtn,
                  (isDemoRunning || demoBusy) && styles.demoBtnDisabled,
                ]}
                disabled={isDemoRunning || demoBusy}
              >
                <Text style={styles.secondaryBtnText}>Roll forward</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.demoDivider} />

            {/* Reset / Remove (danger) */}
            <View style={styles.demoActionRow}>
              <View style={styles.demoActionInfo}>
                <Text style={styles.demoActionTitle}>Reset / Remove</Text>
                <Text style={styles.demoMuted}>
                  Deletes the demo school's data (never Django logins). Type
                  RESET to enable.
                </Text>
              </View>
              <View style={styles.demoResetControls}>
                <TextInput
                  value={demoResetConfirm}
                  onChangeText={setDemoResetConfirm}
                  placeholder="RESET"
                  autoCapitalize="characters"
                  editable={!isDemoRunning && !demoBusy}
                  style={[styles.controlInput, styles.demoResetInput]}
                />
                <TouchableOpacity
                  onPress={startDemoReset}
                  style={[
                    styles.demoDangerBtn,
                    (demoResetConfirm !== "RESET" || isDemoRunning || demoBusy) &&
                      styles.demoBtnDisabled,
                  ]}
                  disabled={
                    demoResetConfirm !== "RESET" || isDemoRunning || demoBusy
                  }
                >
                  <Text style={styles.demoDangerBtnText}>Reset / Remove</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* ── Demo logins (collapsible) ── */}
          {demoAccounts && (
            <View style={styles.adminCard}>
              <TouchableOpacity
                style={styles.demoLoginsToggle}
                onPress={() => setDemoLoginsOpen((o) => !o)}
              >
                <Text style={styles.demoCardTitle}>
                  {demoLoginsOpen ? "▾ Hide logins" : `▸ Show logins (${(demoAccounts.staff?.length || 0) + (demoAccounts.students?.length || 0)})`}
                </Text>
              </TouchableOpacity>

              {demoLoginsOpen && (
                <View style={styles.demoLoginsBody}>
                  {/* Shared password */}
                  <View style={styles.demoPwRow}>
                    <Text style={styles.demoMuted}>Password:</Text>
                    <Text style={styles.demoPwChip} selectable>
                      {demoAccounts.password || "—"}
                    </Text>
                    <TouchableOpacity
                      onPress={() => handleDemoCopy(demoAccounts.password, "pw")}
                      style={styles.demoCopyBtn}
                    >
                      <Text style={styles.demoCopyBtnText}>
                        {demoCopiedKey === "pw" ? "Copied" : "Copy"}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.demoMuted}>
                    Same password for every account.
                  </Text>
                  {demoAccounts.seeded === false && (
                    <Text style={styles.demoWarn}>
                      School not seeded in this environment yet — run Create /
                      Rebuild first.
                    </Text>
                  )}

                  {/* Staff */}
                  {Array.isArray(demoAccounts.staff) &&
                    demoAccounts.staff.length > 0 && (
                      <>
                        <Text style={styles.demoGroupHeading}>Staff</Text>
                        <View style={styles.demoLoginTable}>
                          {demoAccounts.staff.map((s) => (
                            <View key={s.email} style={styles.demoLoginRow}>
                              <View style={styles.demoLoginEmailCell}>
                                <Text
                                  style={styles.demoLoginEmail}
                                  numberOfLines={1}
                                >
                                  {s.email}
                                </Text>
                                <TouchableOpacity
                                  onPress={() => handleDemoCopy(s.email, s.email)}
                                  style={styles.demoCopyBtnSm}
                                >
                                  <Text style={styles.demoCopyBtnText}>
                                    {demoCopiedKey === s.email ? "✓" : "Copy"}
                                  </Text>
                                </TouchableOpacity>
                              </View>
                              <Text
                                style={styles.demoLoginName}
                                numberOfLines={1}
                              >
                                {s.name}
                              </Text>
                              <Text
                                style={styles.demoLoginMeta}
                                numberOfLines={1}
                              >
                                {s.role}
                                {s.classes ? ` · ${s.classes}` : ""}
                              </Text>
                            </View>
                          ))}
                        </View>
                      </>
                    )}

                  {/* Students grouped by archetype */}
                  {demoStudentGroups.map((group) => (
                    <View key={group.archetype}>
                      <Text style={styles.demoGroupHeading}>
                        {group.archetype} ({group.rows.length})
                      </Text>
                      <View style={styles.demoLoginTable}>
                        {group.rows.map((st) => (
                          <View key={st.email} style={styles.demoLoginRow}>
                            <View style={styles.demoLoginEmailCell}>
                              <Text
                                style={styles.demoLoginEmail}
                                numberOfLines={1}
                              >
                                {st.email}
                              </Text>
                              <TouchableOpacity
                                onPress={() => handleDemoCopy(st.email, st.email)}
                                style={styles.demoCopyBtnSm}
                              >
                                <Text style={styles.demoCopyBtnText}>
                                  {demoCopiedKey === st.email ? "✓" : "Copy"}
                                </Text>
                              </TouchableOpacity>
                            </View>
                            <Text
                              style={styles.demoLoginName}
                              numberOfLines={1}
                            >
                              {st.name}
                            </Text>
                            <Text
                              style={styles.demoLoginMeta}
                              numberOfLines={1}
                            >
                              {st.class_name}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}
        </ScrollView>
      )}

      {activeTab === "lesson_plans" && isInternalAdmin && (
        <ScrollView contentContainerStyle={{ paddingBottom: 24 }}>
          <View style={styles.sectionHeader}>
            <Text style={styles.title}>Lesson Plans</Text>
            <View style={styles.sectionActions}>
              <TouchableOpacity
                onPress={fetchLessonSeedStatus}
                style={[
                  styles.secondaryBtn,
                  lessonStatusLoading && styles.demoBtnDisabled,
                ]}
                disabled={lessonStatusLoading}
              >
                <Text style={styles.secondaryBtnText}>Refresh</Text>
              </TouchableOpacity>
            </View>
          </View>

          <Text style={styles.demoDesc}>
            Loads the lesson plan files shipped with the backend into this
            environment's database. Safe to re-run: lessons upsert by slug and
            student progress is keyed on step_key, so nothing is orphaned.
          </Text>

          <View style={styles.adminCard}>
            <View style={styles.demoActionRow}>
              <View style={styles.demoActionInfo}>
                <Text style={styles.demoActionTitle}>
                  Lesson files on this server
                </Text>
                {lessonStatusLoading && !lessonStatus ? (
                  <View style={styles.demoStatusRow}>
                    <ActivityIndicator size="small" color="#0077B6" />
                    <Text style={styles.demoMuted}>Checking lesson files…</Text>
                  </View>
                ) : lessonStatusError ? (
                  <Text style={styles.demoMuted}>{lessonStatusError}</Text>
                ) : (
                  <Text style={styles.demoMuted}>{lessonSummaryLine}</Text>
                )}
              </View>
              <TouchableOpacity
                onPress={seedLessonPlans}
                style={[
                  styles.primaryBtn,
                  !canSeedLessons && styles.demoBtnDisabled,
                ]}
                disabled={!canSeedLessons}
              >
                <Text style={styles.primaryBtnText}>
                  {lessonSeeding ? "Seeding…" : "Seed lesson plans"}
                </Text>
              </TouchableOpacity>
            </View>

            {lessonDirError ? (
              <View style={[styles.demoErrorBox, styles.lessonResultBlock]}>
                <Text style={styles.demoErrorTitle}>
                  No lesson files could be read
                </Text>
                <Text style={styles.demoErrorText} selectable>
                  {lessonDirError}
                </Text>
              </View>
            ) : null}

            {lessonSeedError ? (
              <View style={[styles.demoErrorBox, styles.lessonResultBlock]}>
                <Text style={styles.demoErrorTitle}>Seeding failed</Text>
                <Text style={styles.demoErrorText} selectable>
                  {lessonSeedError}
                </Text>
              </View>
            ) : null}

            {lessonSeedResult ? (
              <View style={styles.lessonResultBlock}>
                <Text
                  style={lessonSeedWrote ? styles.demoSuccess : styles.lessonSeedNeutral}
                >
                  {lessonSeedWrote ? "✓ " : ""}
                  {describeLessonSeed(lessonSeedResult)}
                </Text>
                {lessonSeedResult.results?.length ? (
                  <View style={styles.demoSummaryTable}>
                    {lessonSeedResult.results.map((r) => (
                      <View key={r.file} style={styles.demoSummaryRow}>
                        <Text style={styles.demoSummaryKey} numberOfLines={1}>
                          {r.slug || r.file}
                        </Text>
                        <Text
                          style={[
                            styles.demoSummaryVal,
                            r.action === "skipped" && styles.demoSummaryValLocked,
                          ]}
                        >
                          {describeLessonAction(r.action)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}
              </View>
            ) : null}

            {lessonFiles.length > 0 && (
              <>
                <View style={styles.demoDivider} />
                <View style={styles.demoLoginTable}>
                  {lessonFiles.map((f) => (
                    <View key={f.file} style={styles.demoLoginRow}>
                      <Text style={styles.lessonFileName} numberOfLines={1}>
                        {f.file}
                      </Text>
                      <Text
                        style={
                          f.error ? styles.lessonFileError : styles.demoLoginMeta
                        }
                        numberOfLines={2}
                      >
                        {f.error ||
                          (f.content_locked && f.content_locked_by
                            ? `Edited by ${f.content_locked_by} — this file will not be written`
                            : f.title || f.slug || "—")}
                      </Text>
                      {/* Locked wins over "In database": both are true, but only
                          one of them changes what the button will do. */}
                      <Text
                        style={[
                          styles.lessonBadge,
                          f.content_locked
                            ? styles.lessonBadgeLocked
                            : f.in_database
                            ? styles.lessonBadgeIn
                            : styles.lessonBadgeOut,
                        ]}
                      >
                        {f.content_locked
                          ? "Locked"
                          : f.in_database
                          ? "In database"
                          : "Not seeded"}
                      </Text>
                    </View>
                  ))}
                </View>
              </>
            )}

            <SeededLessonsPanel />
          </View>
        </ScrollView>
      )}

      {activeTab === "lesson_library" && isInternalAdmin && (
        <LessonLibrarySection />
      )}

      <Modal
        visible={planModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setPlanModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setPlanModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>{editingPlan ? "Edit Plan" : "Create Plan"}</Text>

            <TextInput
              placeholder="Plan code (e.g., monthly)"
              value={planForm.code}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, code: v }))}
              editable={!editingPlan && !isModalSubmitting}
              style={[styles.modalInput, editingPlan && styles.disabledInput]}
            />
            <TextInput
              placeholder="Plan name"
              value={planForm.name}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, name: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />
            <TextInput
              placeholder="Duration days (e.g., 30)"
              value={planForm.duration_days}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, duration_days: v.replace(/[^0-9]/g, "") }))}
              editable={!isModalSubmitting}
              keyboardType="numeric"
              style={styles.modalInput}
            />
            <TextInput
              placeholder="Price (USD)"
              value={planForm.price_dollars}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, price_dollars: v }))}
              editable={!isModalSubmitting}
              keyboardType="decimal-pad"
              style={styles.modalInput}
            />
            <TextInput
              placeholder="Region (optional, e.g., US-CA)"
              value={planForm.region}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, region: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />
            <TextInput
              placeholder="Category (optional: student, teacher, or parent)"
              value={planForm.category}
              onChangeText={(v) => setPlanForm((f) => ({ ...f, category: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TouchableOpacity
              onPress={() => setPlanForm((f) => ({ ...f, is_active: !f.is_active }))}
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>Active</Text>
              <Text style={styles.editBtn}>{planForm.is_active ? "Yes" : "No"}</Text>
            </TouchableOpacity>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setPlanModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={savePlan}
                disabled={isModalSubmitting}
              >
                <Text style={[styles.saveBtn, isModalSubmitting && { opacity: 0.4 }]}>
                  {isModalSubmitting ? "Saving..." : "Save"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={discountModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setDiscountModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setDiscountModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>{editingDiscount ? "Edit Discount" : "Create Discount"}</Text>

            <TextInput
              placeholder="Code (e.g., 100OFF)"
              value={discountForm.code}
              onChangeText={(v) => setDiscountForm((f) => ({ ...f, code: v }))}
              editable={!editingDiscount && !isModalSubmitting}
              style={[styles.modalInput, editingDiscount && styles.disabledInput]}
            />

            <View style={[styles.controlShell, { marginBottom: 10 }]}>
              <Picker
                selectedValue={discountForm.discount_type}
                onValueChange={(v) => setDiscountForm((f) => ({ ...f, discount_type: v }))}
                style={[styles.controlPicker, isWeb && styles.controlPickerWeb]}
              >
                <Picker.Item label="Flat" value="flat" />
                <Picker.Item label="Percent" value="percent" />
              </Picker>
            </View>

            {discountForm.discount_type === "flat" ? (
              <TextInput
                placeholder="Amount (USD)"
                value={discountForm.amount_dollars}
                onChangeText={(v) => setDiscountForm((f) => ({ ...f, amount_dollars: v }))}
                editable={!isModalSubmitting}
                keyboardType="decimal-pad"
                style={styles.modalInput}
              />
            ) : (
              <TextInput
                placeholder="Percent off (e.g., 10)"
                value={discountForm.percent_off}
                onChangeText={(v) => setDiscountForm((f) => ({ ...f, percent_off: v.replace(/[^0-9]/g, "") }))}
                editable={!isModalSubmitting}
                keyboardType="numeric"
                style={styles.modalInput}
              />
            )}

            <TextInput
              placeholder="Max redemptions (optional)"
              value={discountForm.max_redemptions}
              onChangeText={(v) => setDiscountForm((f) => ({ ...f, max_redemptions: v.replace(/[^0-9]/g, "") }))}
              editable={!isModalSubmitting}
              keyboardType="numeric"
              style={styles.modalInput}
            />
            <TextInput
              placeholder="Expires (YYYY-MM-DD)"
              value={discountForm.ends_at}
              onChangeText={(v) => setDiscountForm((f) => ({ ...f, ends_at: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TouchableOpacity
              onPress={() => setDiscountForm((f) => ({ ...f, is_active: !f.is_active }))}
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>Active</Text>
              <Text style={styles.editBtn}>{discountForm.is_active ? "Yes" : "No"}</Text>
            </TouchableOpacity>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setDiscountModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={saveDiscount}
                disabled={isModalSubmitting}
              >
                <Text style={[styles.saveBtn, isModalSubmitting && { opacity: 0.4 }]}>
                  {isModalSubmitting ? "Saving..." : "Save"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={bulkUserModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setBulkUserModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setBulkUserModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create Bulk Users</Text>

            {user?.is_internal_admin && (
              <>
                <Text style={styles.label}>Partner (optional)</Text>
                <Picker
                  selectedValue={bulkPartnerId}
                  onValueChange={(v) => {
                    setBulkPartnerId(v);
                    setBulkSchoolId("");
                  }}
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item
                    label={partnerOptionsLoading ? "Loading partners..." : "Select partner"}
                    value=""
                  />
                  {partnerOptions.map((p) => (
                    <Picker.Item
                      key={p.id}
                      label={`${p.name} (${p.code})`}
                      value={String(p.id)}
                    />
                  ))}
                </Picker>
              </>
            )}

            <Text style={styles.label}>School</Text>
            <Picker
              selectedValue={bulkSchoolId}
              onValueChange={setBulkSchoolId}
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item
                label={
                  userSchoolOptionsLoading
                    ? "Loading schools..."
                    : "Select school"
                }
                value=""
              />
              {filteredBulkSchools.map((s) => (
                <Picker.Item
                  key={s.id}
                  label={`${s.name} (${s.code})`}
                  value={String(s.id)}
                />
              ))}
            </Picker>

            <TouchableOpacity
              onPress={pickBulkFile}
              disabled={isModalSubmitting}
              style={styles.primaryBtn}
            >
              <Text style={styles.primaryBtnText}>Choose JSON/CSV File</Text>
            </TouchableOpacity>

            <View style={styles.templateRow}>
              <TouchableOpacity
                onPress={() => downloadBulkTemplate("csv")}
                disabled={isModalSubmitting}
              >
                <Text style={styles.templateBtn}>Download CSV Template</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => downloadBulkTemplate("json")}
                disabled={isModalSubmitting}
              >
                <Text style={styles.templateBtn}>Download JSON Template</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.bulkFileText}>
              {bulkFile?.name ? `Selected: ${bulkFile.name}` : "No file selected"}
            </Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setBulkUserModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={createBulkUsers}
                disabled={!isBulkCreateFormValid() || isModalSubmitting}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (!isBulkCreateFormValid() || isModalSubmitting) && { opacity: 0.4 },
                  ]}
                >
                  {isModalSubmitting ? "Creating..." : "Create Bulk Users"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={[styles.modal, styles.modalScrollable]} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Edit User</Text>

            <ScrollView
              style={styles.modalScrollBody}
              keyboardShouldPersistTaps="handled"
            >

            <TextInput
              value={editingUser?.email || ""}
              editable={false}
              style={[styles.modalInput, styles.disabledInput]}
            />

            <TextInput
              placeholder="First name (optional)"
              value={editUserForm.first_name}
              onChangeText={(v) =>
                setEditUserForm((f) => ({ ...f, first_name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Last name (optional)"
              value={editUserForm.last_name}
              onChangeText={(v) =>
                setEditUserForm((f) => ({ ...f, last_name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <Text style={styles.label}>Role</Text>
            <Picker
              selectedValue={editUserForm.role}
              onValueChange={(v) =>
                setEditUserForm((f) => ({
                  ...f,
                  role: v,
                  partner_id: v === PARTNER_ADMIN_ROLE ? f.partner_id : "",
                  class_role: f.class_id ? f.class_role : "",
                }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              {roleOptions.map((r) => (
                <Picker.Item key={r} label={r} value={r} />
              ))}
            </Picker>

            <Text style={styles.label}>Curriculum</Text>
            <Picker
              selectedValue={editUserForm.content_db}
              onValueChange={(v) =>
                setEditUserForm((f) => ({ ...f, content_db: v }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item label="Select curriculum" value="" />
              {CURRICULA.map((c) => (
                <Picker.Item key={c} label={c} value={c} />
              ))}
            </Picker>

            <Text style={styles.label}>School</Text>
            <Picker
              selectedValue={editUserForm.school_id}
              onValueChange={(v) =>
                setEditUserForm((f) => ({
                  ...f,
                  school_id: v,
                  class_id: "",
                  class_role: "",
                }))
              }
              enabled={!isModalSubmitting && !isSchoolAdmin}
              style={styles.picker}
            >
              <Picker.Item
                label={userSchoolOptionsLoading ? "Loading schools..." : "Select school"}
                value=""
              />
              {userSchoolOptions.map((s) => (
                <Picker.Item
                  key={s.id}
                  label={`${s.name} (${s.code})`}
                  value={String(s.id)}
                />
              ))}
            </Picker>

            <Text style={styles.label}>Class (optional)</Text>
            <Picker
              selectedValue={editUserForm.class_id}
              onValueChange={(v) =>
                setEditUserForm((f) => ({ ...f, class_id: v, class_role: "" }))
              }
              enabled={!isModalSubmitting && !!editUserForm.school_id}
              style={styles.picker}
            >
              <Picker.Item
                label={
                  !editUserForm.school_id
                    ? "Select school first"
                    : classOptionsLoading
                      ? "Loading classes..."
                      : "Select class"
                }
                value=""
              />
              {classOptions.map((c) => (
                <Picker.Item
                  key={c.id}
                  label={`${c.name}`}
                  value={String(c.id)}
                />
              ))}
            </Picker>

            {editUserForm.class_id && (
              <>
                <Text style={styles.label}>Class Role</Text>
                <Picker
                  selectedValue={editUserForm.class_role}
                  onValueChange={(v) =>
                    setEditUserForm((f) => ({ ...f, class_role: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item label="Select class role" value="" />
                  <Picker.Item label="student" value="student" />
                  <Picker.Item label="teacher" value="teacher" />
                </Picker>
              </>
            )}

            {user?.is_internal_admin && editUserForm.role === "partneradmin" && (
              <>
                <Text style={styles.label}>Partner</Text>
                <Picker
                  selectedValue={editUserForm.partner_id}
                  onValueChange={(v) =>
                    setEditUserForm((f) => ({ ...f, partner_id: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item
                    label={partnerOptionsLoading ? "Loading partners..." : "Select partner"}
                    value=""
                  />
                  {partnerOptions.map((p) => (
                    <Picker.Item
                      key={p.id}
                      label={`${p.name} (${p.code})`}
                      value={String(p.id)}
                    />
                  ))}
                </Picker>
              </>
            )}

            <TextInput
              value={editUserForm.grade}
              onChangeText={(v) =>
                setEditUserForm((f) => ({ ...f, grade: v }))
              }
              placeholder="Grade (optional)"
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Science centre (optional)"
              value={editUserForm.science_center}
              onChangeText={(v) =>
                setEditUserForm((f) => ({ ...f, science_center: v }))
              }
              editable={!isModalSubmitting}
              autoCapitalize="none"
              style={styles.modalInput}
            />

            <Text style={styles.label}>Summer Camp User</Text>
            <TouchableOpacity
              onPress={() =>
                !isModalSubmitting &&
                setEditUserForm((f) => ({
                  ...f,
                  is_summer_camp: !f.is_summer_camp,
                }))
              }
              disabled={isModalSubmitting}
              style={styles.inlineToggleBtn}
            >
              <Text style={styles.editBtn}>
                {editUserForm.is_summer_camp ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <Text style={styles.label}>STEM Missions User</Text>
            <TouchableOpacity
              onPress={() =>
                !isModalSubmitting &&
                setEditUserForm((f) => ({
                  ...f,
                  is_stem_missions: !f.is_stem_missions,
                }))
              }
              disabled={isModalSubmitting}
              style={styles.inlineToggleBtn}
            >
              <Text style={styles.editBtn}>
                {editUserForm.is_stem_missions ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            {isInternalAdmin && (
              <TextInput
                placeholder="Duration days (internal admin only)"
                value={editUserForm.duration}
                onChangeText={(v) =>
                  setEditUserForm((f) => ({ ...f, duration: v }))
                }
                editable={!isModalSubmitting}
                keyboardType="numeric"
                style={styles.modalInput}
              />
            )}
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                disabled={
                  isModalSubmitting || !isEditStudentFormValid()
                }
                onPress={saveEdit}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (isModalSubmitting || !isEditStudentFormValid()) &&
                      styles.disabledActionText,
                  ]}
                >
                  {isModalSubmitting ? "Saving..." : "Save"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={createUserModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setCreateUserModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={[styles.modal, styles.modalScrollable]} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setCreateUserModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create User</Text>

            <ScrollView
              style={styles.modalScrollBody}
              keyboardShouldPersistTaps="handled"
            >

            <TextInput
              placeholder="Email"
              value={createUserForm.email}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, email: v }))
              }
              editable={!isModalSubmitting}
              autoCapitalize="none"
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Password"
              value={createUserForm.password}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, password: v }))
              }
              editable={!isModalSubmitting}
              secureTextEntry
              style={styles.modalInput}
            />

            <TextInput
              placeholder="First name (optional)"
              value={createUserForm.first_name}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, first_name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Last name (optional)"
              value={createUserForm.last_name}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, last_name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <Text style={styles.label}>Role</Text>
            <Picker
              selectedValue={createUserForm.role}
              onValueChange={(v) =>
                setCreateUserForm((f) => ({ ...f, role: v }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              {roleOptions.map((r) => (
                <Picker.Item key={r} label={r} value={r} />
              ))}
            </Picker>

            <Text style={styles.label}>Curriculum</Text>
            <Picker
              selectedValue={createUserForm.content_db}
              onValueChange={(v) =>
                setCreateUserForm((f) => ({ ...f, content_db: v }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item label="Select curriculum" value="" />
              {CURRICULA.map((c) => (
                <Picker.Item key={c} label={c} value={c} />
              ))}
            </Picker>

            <Text style={styles.label}>School</Text>
            <Picker
              selectedValue={createUserForm.school_id}
              onValueChange={(v) =>
                setCreateUserForm((f) => ({ ...f, school_id: v, class_id: "" }))
              }
              enabled={!isModalSubmitting && !isSchoolAdmin}
              style={styles.picker}
            >
              <Picker.Item
                label={userSchoolOptionsLoading ? "Loading schools..." : "Select school"}
                value=""
              />
              {userSchoolOptions.map((s) => (
                <Picker.Item
                  key={s.id}
                  label={`${s.name} (${s.code})`}
                  value={String(s.id)}
                />
              ))}
            </Picker>

            <Text style={styles.label}>Class (optional)</Text>
            <Picker
              selectedValue={createUserForm.class_id}
              onValueChange={(v) =>
                setCreateUserForm((f) => ({ ...f, class_id: v, class_role: "" }))
              }
              enabled={!isModalSubmitting && !!createUserForm.school_id}
              style={styles.picker}
            >
              <Picker.Item
                label={
                  !createUserForm.school_id
                    ? "Select school first"
                    : classOptionsLoading
                      ? "Loading classes..."
                      : "Select class"
                }
                value=""
              />
              {classOptions.map((c) => (
                <Picker.Item
                  key={c.id}
                  label={`${c.name}`}
                  value={String(c.id)}
                />
              ))}
            </Picker>

            {createUserForm.class_id && (
              <>
                <Text style={styles.label}>Class Role</Text>
                <Picker
                  selectedValue={createUserForm.class_role}
                  onValueChange={(v) =>
                    setCreateUserForm((f) => ({ ...f, class_role: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item label="Select class role" value="" />
                  <Picker.Item label="student" value="student" />
                  <Picker.Item label="teacher" value="teacher" />
                </Picker>
              </>
            )}

            {user?.is_internal_admin && createUserForm.role === "partneradmin" && (
              <>
                <Text style={styles.label}>Partner</Text>
                <Picker
                  selectedValue={createUserForm.partner_id}
                  onValueChange={(v) =>
                    setCreateUserForm((f) => ({ ...f, partner_id: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item
                    label={partnerOptionsLoading ? "Loading partners..." : "Select partner"}
                    value=""
                  />
                  {partnerOptions.map((p) => (
                    <Picker.Item
                      key={p.id}
                      label={`${p.name} (${p.code})`}
                      value={String(p.id)}
                    />
                  ))}
                </Picker>
              </>
            )}

            <TextInput
              placeholder="Grade (optional)"
              value={createUserForm.grade}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, grade: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Science centre (optional)"
              value={createUserForm.science_center}
              onChangeText={(v) =>
                setCreateUserForm((f) => ({ ...f, science_center: v }))
              }
              editable={!isModalSubmitting}
              autoCapitalize="none"
              style={styles.modalInput}
            />

            <Text style={styles.label}>Summer Camp User</Text>
            <TouchableOpacity
              onPress={() =>
                !isModalSubmitting &&
                setCreateUserForm((f) => ({
                  ...f,
                  is_summer_camp: !f.is_summer_camp,
                }))
              }
              disabled={isModalSubmitting}
              style={styles.inlineToggleBtn}
            >
              <Text style={styles.editBtn}>
                {createUserForm.is_summer_camp ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <Text style={styles.label}>STEM Missions User</Text>
            <TouchableOpacity
              onPress={() =>
                !isModalSubmitting &&
                setCreateUserForm((f) => ({
                  ...f,
                  is_stem_missions: !f.is_stem_missions,
                }))
              }
              disabled={isModalSubmitting}
              style={styles.inlineToggleBtn}
            >
              <Text style={styles.editBtn}>
                {createUserForm.is_stem_missions ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            {isInternalAdmin && (
              <TextInput
                placeholder="Duration days (internal admin only)"
                value={createUserForm.duration}
                onChangeText={(v) =>
                  setCreateUserForm((f) => ({ ...f, duration: v }))
                }
                editable={!isModalSubmitting}
                keyboardType="numeric"
                style={styles.modalInput}
              />
            )}
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setCreateUserModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={createStudent}
                disabled={!isCreateStudentFormValid() || isModalSubmitting}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (!isCreateStudentFormValid() || isModalSubmitting) && { opacity: 0.4 },
                  ]}
                >
                  {isModalSubmitting ? "Creating..." : "Create"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={drModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setDrModalVisible(false)}
      >
        <View
          style={styles.modalBackdrop}
        >
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setDrModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create Data Request</Text>

            {/* Target Type */}
            <Text style={styles.label}>Target Type</Text>
            <Picker
              selectedValue={drForm.target_type}
              onValueChange={(v) =>
                setDrForm((f) => ({
                  ...f,
                  target_type: v,
                  student_email: "",
                  class_name: "",
                  school_name: "",
                }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item label="Student" value="student" />
              <Picker.Item label="Class" value="class" />
              <Picker.Item label="School" value="school" />
            </Picker>

            {/* Target fields */}
            {drForm.target_type === "student" && (
              <TextInput
                placeholder="Student email"
                value={drForm.student_email}
                onChangeText={(v) =>
                  setDrForm((f) => ({ ...f, student_email: v }))
                }
                editable={!isModalSubmitting}
                autoCapitalize="none"
                style={styles.modalInput}
              />
            )}

            {drForm.target_type === "class" && (
              <TextInput
                placeholder="Class name"
                value={drForm.class_name}
                onChangeText={(v) =>
                  setDrForm((f) => ({ ...f, class_name: v }))
                }
                editable={!isModalSubmitting}
                style={styles.modalInput}
              />
            )}

            {drForm.target_type === "school" && (
              <TextInput
                placeholder="School name"
                value={drForm.school_name}
                onChangeText={(v) =>
                  setDrForm((f) => ({ ...f, school_name: v }))
                }
                editable={!isModalSubmitting}
                style={styles.modalInput}
              />
            )}

            {/* Requester */}
            <Text style={styles.label}>Requester Type</Text>
            <Picker
              selectedValue={drForm.requester_type}
              onValueChange={(v) =>
                setDrForm((f) => ({ ...f, requester_type: v }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item label="Student" value="student" />
              <Picker.Item label="Parent" value="parent" />
              <Picker.Item label="Teacher" value="teacher" />
              <Picker.Item label="Other" value="other" />
            </Picker>

            <TextInput
              placeholder="Requester email (optional)"
              value={drForm.requester_email}
              onChangeText={(v) =>
                setDrForm((f) => ({ ...f, requester_email: v }))
              }
              editable={!isModalSubmitting}
              autoCapitalize="none"
              style={styles.modalInput}
            />

            {/* Request Type */}
            <Text style={styles.label}>Request Type</Text>
            <Picker
              selectedValue={drForm.request_type}
              onValueChange={(v) =>
                setDrForm((f) => ({
                  ...f,
                  request_type: v,
                  retention_days: v === "soft_delete" ? f.retention_days : "",
                }))
              }
              enabled={!isModalSubmitting}
              style={styles.picker}
            >
              <Picker.Item label="Access" value="access" />
              <Picker.Item label="Soft Delete" value="soft_delete" />
              <Picker.Item label="Hard Delete" value="hard_delete" />
            </Picker>

            {drForm.request_type === "soft_delete" && (
              <>
                <Text style={styles.label}>Retention Days</Text>
                <TextInput
                  placeholder="Retention days (e.g. 30)"
                  value={drForm.retention_days}
                  onChangeText={(v) =>
                    setDrForm((f) => ({
                      ...f,
                      retention_days: v.replace(/[^0-9]/g, ""),
                    }))
                  }
                  editable={!isModalSubmitting}
                  keyboardType="numeric"
                  style={styles.modalInput}
                />
              </>
            )}

            {Platform.OS === "web" ? (
              <input
                type="date"
                value={drForm.data_from?.slice(0, 10) || ""}
                onChange={(e) =>
                  setDrForm((f) => ({
                    ...f,
                    data_from: e.target.value
                      ? new Date(e.target.value + "T00:00:00Z").toISOString()
                      : "",
                  }))
                }
                disabled={isModalSubmitting}
                style={{
                  ...styles.modalInput,
                  height: 44,
                  fontSize: 14,
                }}
              />
            ) : (
              <TextInput
                value={drForm.data_from}
                editable={!isModalSubmitting}
                style={styles.modalInput}
              />
            )}

           {Platform.OS === "web" ? (
              <input
                type="date"
                value={drForm.data_to?.slice(0, 10) || ""}
                onChange={(e) =>
                  setDrForm((f) => ({
                    ...f,
                    data_to: e.target.value
                      ? new Date(e.target.value + "T23:59:59Z").toISOString()
                      : "",
                  }))
                }
                disabled={isModalSubmitting}
                style={{
                  ...styles.modalInput,
                  height: 44,
                  fontSize: 14,
                }}
              />
            ) : (
              <TextInput
                value={drForm.data_to}
                editable={!isModalSubmitting}
                style={styles.modalInput}
              />
            )}

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setDrModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={submitDataRequest}
                disabled={!isDrFormValid() || isModalSubmitting}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (!isDrFormValid() || isModalSubmitting) && { opacity: 0.4 },
                  ]}
                >
                  {isModalSubmitting ? "Submitting..." : "Submit"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={schoolModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setSchoolModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setSchoolModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create School</Text>

            <TextInput
              placeholder="School name"
              value={schoolForm.name}
              onChangeText={(v) => setSchoolForm((f) => ({ ...f, name: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="School code"
              value={schoolForm.code}
              onChangeText={(v) => setSchoolForm((f) => ({ ...f, code: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            {user?.is_internal_admin && (
              <>
                <Text style={styles.label}>Partner (optional)</Text>
                <Picker
                  selectedValue={schoolForm.partner_id}
                  onValueChange={(v) =>
                    setSchoolForm((f) => ({ ...f, partner_id: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                  <Picker.Item
                    label={partnerOptionsLoading ? "Loading partners..." : "Select partner"}
                    value=""
                  />
                  {partnerOptions.map((p) => (
                    <Picker.Item
                      key={p.id}
                      label={`${p.name} (${p.code})`}
                      value={String(p.id)}
                    />
                  ))}
                </Picker>

                <Text style={styles.label}>Billing Cycle (days)</Text>
                <Picker
                  selectedValue={schoolForm.billing_cycle_days}
                  onValueChange={(v) =>
                    setSchoolForm((f) => ({ ...f, billing_cycle_days: v }))
                  }
                  enabled={!isModalSubmitting}
                  style={styles.picker}
                >
                <Picker.Item label="Select cycle" value="" />
                <Picker.Item label="Monthly (30)" value="30" />
                <Picker.Item label="Quarterly (122)" value="122" />
                <Picker.Item label="Semi-Annual (183)" value="183" />
                <Picker.Item label="Annual (365)" value="365" />
                </Picker>

              </>
            )}

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setSchoolModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={createSchool}
                disabled={
                  isModalSubmitting ||
                  !schoolForm.name.trim() ||
                  !schoolForm.code.trim() ||
                  (user?.is_internal_admin &&
                    !!schoolForm.partner_id &&
                    !schoolForm.billing_cycle_days)
                }
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (
                      !schoolForm.name.trim() ||
                      !schoolForm.code.trim() ||
                      isModalSubmitting ||
                      (user?.is_internal_admin &&
                        !!schoolForm.partner_id &&
                        !schoolForm.billing_cycle_days)
                    ) && {
                      opacity: 0.4,
                    },
                  ]}
                >
                  {isModalSubmitting ? "Creating..." : "Create"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={classModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setClassModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setClassModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create Class</Text>

            <TextInput
              placeholder="Class name"
              value={classForm.name}
              onChangeText={(v) => setClassForm((f) => ({ ...f, name: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Class code (optional)"
              value={classForm.code}
              onChangeText={(v) => setClassForm((f) => ({ ...f, code: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <Text style={styles.label}>School</Text>
            <Picker
              selectedValue={classForm.school_id}
              onValueChange={(v) => setClassForm((f) => ({ ...f, school_id: v }))}
              enabled={!isModalSubmitting && !isSchoolAdmin}
              style={styles.picker}
            >
              <Picker.Item
                label={userSchoolOptionsLoading ? "Loading schools..." : "Select school"}
                value=""
              />
              {userSchoolOptions.map((s) => (
                <Picker.Item
                  key={s.id}
                  label={`${s.name} (${s.code})`}
                  value={String(s.id)}
                />
              ))}
            </Picker>

            <TextInput
              placeholder="Default class password (optional)"
              value={classForm.default_password}
              onChangeText={(v) =>
                setClassForm((f) => ({ ...f, default_password: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TouchableOpacity
              onPress={() =>
                setClassForm((f) => ({ ...f, is_active: !f.is_active }))
              }
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>Active</Text>
              <Text style={styles.editBtn}>
                {classForm.is_active ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setClassModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={createClass}
                disabled={
                  isModalSubmitting ||
                  !classForm.name.trim() ||
                  !classForm.school_id
                }
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (
                      !classForm.name.trim() ||
                      !classForm.school_id ||
                      isModalSubmitting
                    ) && {
                      opacity: 0.4,
                    },
                  ]}
                >
                  {isModalSubmitting ? "Creating..." : "Create"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={classEditModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setClassEditModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setClassEditModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Edit Class</Text>

            <TextInput
              placeholder="Class name"
              value={editClassForm.name}
              onChangeText={(v) =>
                setEditClassForm((f) => ({ ...f, name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Class code (optional)"
              value={editClassForm.code}
              onChangeText={(v) =>
                setEditClassForm((f) => ({ ...f, code: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Default class password (optional)"
              value={editClassForm.default_password}
              onChangeText={(v) =>
                setEditClassForm((f) => ({ ...f, default_password: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TouchableOpacity
              onPress={() =>
                setEditClassForm((f) => ({ ...f, is_active: !f.is_active }))
              }
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>Active</Text>
              <Text style={styles.editBtn}>
                {editClassForm.is_active ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setClassEditModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={updateClass}
                disabled={isModalSubmitting || !editClassForm.name.trim()}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (isModalSubmitting || !editClassForm.name.trim()) && {
                      opacity: 0.4,
                    },
                  ]}
                >
                  {isModalSubmitting ? "Saving..." : "Save"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={partnerModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setPartnerModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setPartnerModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Create Partner</Text>

            <TextInput
              placeholder="Partner name"
              value={partnerForm.name}
              onChangeText={(v) => setPartnerForm((f) => ({ ...f, name: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Partner code"
              value={partnerForm.code}
              onChangeText={(v) => setPartnerForm((f) => ({ ...f, code: v }))}
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="Contact email (optional)"
              value={partnerForm.contact_email}
              onChangeText={(v) =>
                setPartnerForm((f) => ({ ...f, contact_email: v }))
              }
              editable={!isModalSubmitting}
              autoCapitalize="none"
              style={styles.modalInput}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setPartnerModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={createPartner}
                disabled={isModalSubmitting || !partnerForm.name.trim() || !partnerForm.code.trim()}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (isModalSubmitting || !partnerForm.name.trim() || !partnerForm.code.trim()) && {
                      opacity: 0.4,
                    },
                  ]}
                >
                  {isModalSubmitting ? "Creating..." : "Create"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={schoolEditModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setSchoolEditModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setSchoolEditModalVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Edit School</Text>

            <TextInput
              placeholder="School name"
              value={editSchoolForm.name}
              onChangeText={(v) =>
                setEditSchoolForm((f) => ({ ...f, name: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TextInput
              placeholder="School code"
              value={editSchoolForm.code}
              onChangeText={(v) =>
                setEditSchoolForm((f) => ({ ...f, code: v }))
              }
              editable={!isModalSubmitting}
              style={styles.modalInput}
            />

            <TouchableOpacity
              onPress={() =>
                setEditSchoolForm((f) => ({ ...f, is_k12: !f.is_k12 }))
              }
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>K12</Text>
              <Text style={styles.editBtn}>
                {editSchoolForm.is_k12 ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() =>
                setEditSchoolForm((f) => ({
                  ...f,
                  requires_restricted_mode: !f.requires_restricted_mode,
                }))
              }
              disabled={isModalSubmitting}
              style={styles.toggleRow}
            >
              <Text style={styles.toggleLabel}>Restricted Mode</Text>
              <Text style={styles.editBtn}>
                {editSchoolForm.requires_restricted_mode ? "Yes" : "No"}
              </Text>
            </TouchableOpacity>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setSchoolEditModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={updateSchool}
                disabled={isModalSubmitting || !editSchoolForm.name.trim() || !editSchoolForm.code.trim()}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (isModalSubmitting || !editSchoolForm.name.trim() || !editSchoolForm.code.trim()) && {
                      opacity: 0.4,
                    },
                  ]}
                >
                  {isModalSubmitting ? "Saving..." : "Save"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={partialPaymentVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setPartialPaymentVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setPartialPaymentVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>
              {partialPaymentMode === "record" ? "Record Payment Only" : "Partial Payment"}
            </Text>

            <TextInput
              placeholder={
                partialPaymentMax
                  ? `Days paid (max ${partialPaymentMax})`
                  : "Days paid (e.g. 80)"
              }
              value={partialPaymentDays}
              onChangeText={(v) =>
                setPartialPaymentDays(v.replace(/[^0-9]/g, ""))
              }
              editable={!isModalSubmitting}
              keyboardType="numeric"
              style={styles.modalInput}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setPartialPaymentVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  const days = Number(partialPaymentDays || 0);
                  if (!days) {
                    showAlert("Error", "Enter days paid");
                    return;
                  }
                  if (partialPaymentMax && days > partialPaymentMax) {
                    showAlert("Error", `Days cannot exceed ${partialPaymentMax}`);
                    return;
                  }
                  const sid = partialPaymentSchoolId;
                  setPartialPaymentVisible(false);
                  receiveBilling(sid, partialPaymentMode, days);
                }}
                disabled={isModalSubmitting || !partialPaymentDays}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    (isModalSubmitting || !partialPaymentDays) && { opacity: 0.4 },
                  ]}
                >
                  {isModalSubmitting ? "Submitting..." : "Apply"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={campPaymentLinkModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !campPaymentLinkSubmitting && setCampPaymentLinkModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !campPaymentLinkSubmitting && setCampPaymentLinkModalVisible(false)}
              disabled={campPaymentLinkSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Enroll in WonderLabs / Get Link</Text>
            <ScrollView style={{ maxHeight: 360 }} keyboardShouldPersistTaps="handled">
              <Text style={styles.label}>Student name *</Text>
              <TextInput
                placeholder="Student name *"
                value={campPaymentLinkForm.student_name}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, student_name: v }))}
                keyboardType="default"
                maxLength={255}
                autoCapitalize="none"
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
              <Text style={styles.label}>Parent name *</Text>
              <TextInput
                placeholder="Parent name *"
                value={campPaymentLinkForm.parent_name}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, parent_name: v }))}
                keyboardType="default"
                maxLength={255}
                autoCapitalize="none"
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
              <Text style={styles.label}>Shipping address (include city, state, ZIP and country) *</Text>
              <TextInput
                placeholder="Shipping address (include city, state, ZIP and country) *"
                value={campPaymentLinkForm.shipping_address}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, shipping_address: v }))}
                keyboardType="default"
                maxLength={2000}
                multiline
                numberOfLines={4}
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
              <Text style={styles.label}>Parent email *</Text>
              <TextInput
                placeholder="Parent email *"
                value={campPaymentLinkForm.parent_email}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, parent_email: v }))}
                keyboardType="email-address"
                maxLength={255}
                autoCapitalize="none"
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
              <Text style={styles.label}>Contact number (optional)</Text>
              <TextInput
                placeholder="Contact number (optional)"
                value={campPaymentLinkForm.parent_contact_number}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, parent_contact_number: v }))}
                keyboardType="phone-pad"
                maxLength={64}
                autoCapitalize="none"
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
              <Text style={styles.label}>Price in USD *</Text>
              <TextInput
                placeholder="295.00"
                value={campPaymentLinkForm.amount_dollars}
                onChangeText={(v) => setCampPaymentLinkForm((f) => ({ ...f, amount_dollars: v }))}
                keyboardType="decimal-pad"
                editable={!campPaymentLinkSubmitting && !campPaymentLinkResult?.id}
                style={styles.modalInput}
              />
            </ScrollView>

            {campPaymentLinkResult?.checkout_url ? (
              <>
                <Text style={styles.label}>Generated Link</Text>
                <TextInput
                  style={styles.modalInput}
                  value={campPaymentLinkResult.checkout_url}
                  editable={false}
                  multiline
                />
              </>
            ) : null}

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={campPaymentLinkSubmitting}
                onPress={() => !campPaymentLinkSubmitting && setCampPaymentLinkModalVisible(false)}
              >
                <Text style={[styles.cancelBtn, campPaymentLinkSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => createCampPaymentLink(false)}
                disabled={campPaymentLinkSubmitting}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    campPaymentLinkSubmitting && { opacity: 0.4 },
                  ]}
                >
                  {campPaymentLinkSubmitting ? "Working..." : "Get Link"}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => createCampPaymentLink(true)}
                disabled={campPaymentLinkSubmitting}
              >
                <Text
                  style={[
                    styles.saveBtn,
                    campPaymentLinkSubmitting && { opacity: 0.4 },
                  ]}
                >
                  {campPaymentLinkSubmitting ? "Working..." : "Send Email"}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>

      <Modal
        visible={paymentChoiceVisible}
        transparent
        animationType="fade"
        onRequestClose={() => !isModalSubmitting && setPaymentChoiceVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable style={styles.modal} onPress={() => {}}>
            <Pressable
              onPress={() => !isModalSubmitting && setPaymentChoiceVisible(false)}
              disabled={isModalSubmitting}
              hitSlop={12}
              style={styles.closeIcon}
            >
              <Text style={styles.closeText}>X</Text>
            </Pressable>

            <Text style={styles.modalTitle}>Confirm Payment</Text>
            <Text style={styles.label}>Is this a full payment or partial?</Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                disabled={isModalSubmitting}
                onPress={() => !isModalSubmitting && setPaymentChoiceVisible(false)}
              >
                <Text style={[styles.cancelBtn, isModalSubmitting && styles.disabledActionText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  const sid = paymentChoiceSchoolId;
                  setPaymentChoiceVisible(false);
                  receiveBilling(sid, "full");
                }}
                disabled={isModalSubmitting}
              >
                <Text style={[styles.saveBtn, isModalSubmitting && { opacity: 0.4 }]}>Full Payment</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => {
                  const sid = paymentChoiceSchoolId;
                  setPaymentChoiceVisible(false);
                  setPartialPaymentSchoolId(sid);
                  setPartialPaymentDays("");
                  setPartialPaymentMode("partial");
                  setPartialPaymentMax(null);
                  setPartialPaymentVisible(true);
                }}
                disabled={isModalSubmitting}
              >
                <Text style={[styles.saveBtn, isModalSubmitting && { opacity: 0.4 }]}>Partial Payment</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </View>
      </Modal>
    </View>
  );
};

export default InternalAdminPanel;


const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: "#fff" },
  title: { fontSize: 20, fontWeight: "700", marginBottom: 0 },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: 8,
    marginTop: 0,
    marginBottom: 12,
  },
  sectionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  toolbarRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  toolbarRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
    flexWrap: "wrap",
  },

  controlInput: {
    height: 36,
    minHeight: 36,
    borderWidth: 1,
    borderColor: "#d8e0e8",
    borderRadius: 7,
    paddingHorizontal: 10,
    fontSize: 12,
    backgroundColor: "#fff",
    color: "#25313d",
  },
  controlShell: {
    height: 36,
    minHeight: 36,
    borderWidth: 1,
    borderColor: "#d8e0e8",
    borderRadius: 7,
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  controlPicker: {
    height: 34,
    fontSize: 12,
  },
  controlPickerWeb: {
    borderWidth: 0,
    outlineStyle: "none",
    backgroundColor: "transparent",
  },
  controlCompact: {
    width: 170,
    minWidth: 150,
    maxWidth: 200,
    flexGrow: 0,
  },

  searchBtnCompact: {
    backgroundColor: "#0077B6",
    borderRadius: 7,
    paddingHorizontal: 12,
    height: 36,
    minHeight: 36,
    justifyContent: "center",
    alignItems: "center",
  },
  searchInputCompact: {
    width: 240,
    minWidth: 200,
    maxWidth: 280,
    flexGrow: 0,
  },
  searchBtnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  primaryBtn: {
    backgroundColor: "#0077B6",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: "center",
    marginBottom: 0,
  },
  primaryBtnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: "#c6d3df",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#fff",
    alignSelf: "center",
  },
  secondaryBtnText: {
    color: "#355065",
    fontWeight: "700",
    fontSize: 12,
  },
  adminCard: {
    borderWidth: 1,
    borderColor: "#e6edf2",
    borderRadius: 12,
    backgroundColor: "#fff",
    padding: 14,
    marginBottom: 14,
  },
  scienceCenterCodeInput: {
    width: 220,
    minWidth: 180,
  },
  scienceCenterPickerShell: {
    width: 360,
    minWidth: 240,
    maxWidth: 420,
  },
  sccField: {
    marginBottom: 8,
  },
  sccFieldLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#5a6772",
    marginBottom: 3,
  },
  sccDayShell: {
    width: 110,
    minWidth: 90,
  },
  sccPctInput: {
    width: 90,
    minWidth: 70,
  },
  sccSubjectInput: {
    width: 180,
    minWidth: 140,
  },
  sccGroup: {
    paddingTop: 8,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#dde6ee",
  },
  sccGroupTitle: {
    fontSize: 13,
    fontWeight: "800",
    color: "#22313f",
    marginBottom: 2,
  },
  mappingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#eef3f7",
  },
  mappingCopy: {
    flex: 1,
  },
  mappingTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#22313f",
    marginBottom: 2,
  },
  mappingMeta: {
    fontSize: 12,
    color: "#5a6772",
  },

  row: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    alignItems: "center",
  },
  headerRow: { backgroundColor: "#f3f6f9", borderBottomWidth: 2 },
  tableRow: { width: "100%" },
  tableViewport: { width: "100%" },
  tableViewportContent: { flexGrow: 1, width: "100%" },
  tableBox: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#e6edf2",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  cell: {
    fontSize: 12,
    color: "#263238",
  },
  engagementText: {
    lineHeight: 16,
  },
  colCell: { paddingHorizontal: 8 },
  cellAlignLeft: { textAlign: "left" },
  cellAlignCenter: { textAlign: "center" },
  headerCell: {
    paddingHorizontal: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    fontWeight: "700",
    fontSize: 12,
    textAlign: "center",
    color: "#2b3c4a",
  },
  centerCell: { alignItems: "center", justifyContent: "center" },

  editBtn: { color: "#0077B6", fontWeight: "700" },
  actionStack: { flexDirection: "column", alignItems: "center", gap: 6 },
  linkBtnSmall: {
    color: "#0077B6",
    fontSize: 11,
    textDecorationLine: "underline",
  },
  empty: { marginTop: 20, textAlign: "center", color: "#777" },

  pagination: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  tableMeta: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 6,
    paddingHorizontal: 8,
  },
  sectionSubtitle: {
    fontSize: 13,
    fontWeight: "700",
    marginTop: 8,
    marginBottom: 8,
    color: "#2b3c4a",
  },
  paymentsNote: {
    fontSize: 12,
    color: "#5a6772",
    marginBottom: 12,
    paddingHorizontal: 8,
  },
  billingTabs: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 12,
  },
  billingFilterRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  billingTabBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: "#eef3f7",
  },
  billingTabActive: {
    backgroundColor: "#0077B6",
  },
  billingTabText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#2b3c4a",
  },
  billingTabTextActive: {
    color: "#fff",
  },
  billingActionColumn: {
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  paginationActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  countText: {
    fontSize: 12,
    color: "#5a6772",
    fontWeight: "600",
  },
  pageBtn: { color: "#0077B6", fontWeight: "700" },
  disabled: { color: "#ccc" },

  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modal: {
    width: "92%",
    maxWidth: 760,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
  },
  modalScrollable: { maxHeight: "90%" },
  modalScrollBody: { flexShrink: 1 },
  closeIcon: {
    position: "absolute",
    right: 10,
    top: 10,
    zIndex: 10,
    elevation: 10,
    padding: 6,
  },
  closeText: {
    fontSize: 18,
    fontWeight: "700",
  },
  modalTitle: { fontSize: 16, fontWeight: "700", marginBottom: 12 },
  inlineToggleBtn: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: "#d0d7de",
    borderRadius: 10,
    alignSelf: "flex-start",
    marginBottom: 10,
  },
  modalInput: {
    height: 46,
    minHeight: 46,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 0,
    fontSize: 14,
    marginBottom: 10,
  },
  disabledInput: { backgroundColor: "#f1f1f1", color: "#777" },
  label: { fontSize: 12, marginBottom: 6, color: "#444" },
  templateRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginTop: 10,
    marginBottom: 2,
    flexWrap: "wrap",
  },
  templateBtn: {
    color: "#0077B6",
    fontWeight: "700",
    fontSize: 12,
  },
  bulkFileText: { fontSize: 12, color: "#555", marginTop: 8, marginBottom: 4 },
  picker: {
    height: 46,
    minHeight: 46,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    backgroundColor: "#fff",
    marginBottom: 10,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 16,
    marginTop: 8,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  toggleLabel: { fontSize: 12, color: "#444" },
  cancelBtn: { color: "#777" },
  saveBtn: { color: "#0077B6", fontWeight: "700" },
  disabledActionText: { opacity: 0.45 },
  tabsScroll: {
    flexGrow: 0,
    minHeight: 52,
    marginBottom: 12,
    zIndex: 20,
    elevation: 20,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    overflow: "visible",
  },
  tabs: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 0,
    minWidth: "100%",
    minHeight: 52,
    backgroundColor: "#fff",
  },
  tabBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    marginRight: 12,
    justifyContent: "center",
    minHeight: 44,
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: "#0077B6",
  },
  tabText: {
    fontSize: 14,
    lineHeight: 18,
    color: "#777",
    fontWeight: "600",
  },
  tabTextActive: {
    color: "#0077B6",
    fontWeight: "700",
  },
  exportBtn: {
    color: "#28a745",
    fontWeight: "700",
  },

  // ── Demo School ──
  demoDesc: { fontSize: 13, color: "#55636f", marginBottom: 14, lineHeight: 18 },
  demoCardTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#25313d",
    marginBottom: 10,
  },
  demoStatusRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  demoMuted: { fontSize: 12, color: "#6b7785", lineHeight: 16 },
  demoRunning: { fontSize: 13, fontWeight: "600", color: "#0077B6" },
  demoSuccess: {
    fontSize: 13,
    fontWeight: "700",
    color: "#1a7f4b",
    marginBottom: 8,
  },
  demoErrorBox: {
    borderWidth: 1,
    borderColor: "#f2c4c4",
    backgroundColor: "#fdf3f3",
    borderRadius: 8,
    padding: 10,
  },
  demoErrorTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#c0392b",
    marginBottom: 4,
  },
  demoErrorText: { fontSize: 12, color: "#a5342a", lineHeight: 16 },
  demoSummaryTable: {
    borderWidth: 1,
    borderColor: "#e6edf2",
    borderRadius: 8,
    marginTop: 4,
    overflow: "hidden",
  },
  demoSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eef2f5",
  },
  demoSummaryKey: { fontSize: 12, color: "#355065" },
  demoSummaryVal: { fontSize: 12, fontWeight: "700", color: "#25313d" },
  demoActionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  demoActionInfo: { flex: 1, minWidth: 200 },
  demoActionTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#25313d",
    marginBottom: 2,
  },
  demoOptions: { marginTop: 12, gap: 8 },
  demoCheckRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  demoCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#c6d3df",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  demoCheckboxOn: { backgroundColor: "#0077B6", borderColor: "#0077B6" },
  demoCheckMark: { color: "#fff", fontSize: 12, fontWeight: "700", lineHeight: 14 },
  demoCheckLabel: { fontSize: 12, color: "#355065" },
  demoPasswordInput: { width: 240, maxWidth: 320, marginTop: 2 },
  demoInputDisabled: { backgroundColor: "#f1f4f7", color: "#9aa7b3" },
  demoStartBtn: { alignSelf: "flex-start", marginTop: 4 },
  demoDivider: { height: 1, backgroundColor: "#eef2f5", marginVertical: 14 },
  demoResetControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  demoResetInput: { width: 120 },
  demoDangerBtn: {
    backgroundColor: "#c0392b",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: "center",
  },
  demoDangerBtnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  demoBtnDisabled: { opacity: 0.45 },

  // ── Demo logins ──
  demoLoginsToggle: { paddingVertical: 2 },
  demoLoginsBody: { marginTop: 10, gap: 6 },
  demoPwRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  demoPwChip: {
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
    fontSize: 12,
    color: "#25313d",
    backgroundColor: "#f1f4f7",
    borderWidth: 1,
    borderColor: "#e6edf2",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  demoWarn: {
    fontSize: 12,
    color: "#8a5a00",
    backgroundColor: "#fff6e6",
    borderWidth: 1,
    borderColor: "#f2dfb0",
    borderRadius: 6,
    padding: 6,
    marginTop: 2,
    lineHeight: 16,
  },
  demoCopyBtn: {
    borderWidth: 1,
    borderColor: "#c6d3df",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: "#fff",
  },
  demoCopyBtnSm: {
    borderWidth: 1,
    borderColor: "#c6d3df",
    borderRadius: 5,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: "#fff",
  },
  demoCopyBtnText: { fontSize: 11, fontWeight: "700", color: "#355065" },
  demoGroupHeading: {
    fontSize: 12,
    fontWeight: "800",
    color: "#0077B6",
    marginTop: 10,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  demoLoginTable: {
    borderWidth: 1,
    borderColor: "#e6edf2",
    borderRadius: 8,
    overflow: "hidden",
  },
  demoLoginRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eef2f5",
  },
  demoLoginEmailCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 2,
    minWidth: 180,
  },
  demoLoginEmail: { fontSize: 12, color: "#25313d", flexShrink: 1 },
  demoLoginName: { fontSize: 12, color: "#355065", flex: 1.2, minWidth: 90 },
  demoLoginMeta: { fontSize: 12, color: "#6b7785", flex: 1.4, minWidth: 90 },

  // ── Lesson Plans ──
  lessonResultBlock: { marginTop: 12 },
  lessonFileName: { fontSize: 12, color: "#25313d", flex: 2, minWidth: 170 },
  lessonFileError: {
    fontSize: 12,
    color: "#c0392b",
    flex: 1.4,
    minWidth: 90,
    lineHeight: 16,
  },
  lessonBadge: {
    fontSize: 11,
    fontWeight: "700",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    overflow: "hidden",
  },
  lessonBadgeIn: { color: "#1a7f4b", backgroundColor: "#e8f6ee" },
  lessonBadgeOut: { color: "#6b7785", backgroundColor: "#f1f4f7" },
  lessonBadgeLocked: { color: "#0b6b8f", backgroundColor: "#e6f2f8" },
  // A run that wrote nothing gets no tick and no green.
  lessonSeedNeutral: { fontSize: 12, color: "#355065", fontWeight: "600" },
  demoSummaryValLocked: { color: "#0b6b8f" },
});

